// Import react elements
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

// Import context function
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";

import {
    getPodProduct,
    getPodProductPrices,
    getPodDesignProducts,
    deleteDesignProduct,
    updatePodDesignProducts
} from "api/APIStrichCalls";

// Import hooks
import { useToast } from "global/hooks/useToast";

// Import components
import ToggleSwitch from "global/components/ToggleSwitch"
import { PodProductPreview } from "global/components/PodProductPreview";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import ImageCarouselPopup from "global/components/ImageCarouselPopup";
import ProductDetailsPopUp from "./ProductDetailsPopUp";
import CreateButton from "global/components/CreateButton";
import LoadingDotsAnimation from "global/components/loading_ui/LoadingDotsAnimation";

// Import utilities
import { S2B } from "global/utilities/BooleanConverters";
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import {
    buildDefaultColourwaySelection,
    getAvailableProductColourwayCodes
} from "../utilities/PodProductColourwayHelpers";
import {
    getAvailableMockupConfigurationsForConfiguredRoles,
    getPreferredMockupConfigurationsForConfiguredRoles,
    getFirstConfiguredProductPreviewView
} from "../utilities/PodProductViewSettingsHelpers";
import { InfoButton } from "global/components/InfoButton";
import { DualRangeSlider } from "global/components/DualRangeSlider";

// Import icons
import { WarningIcon } from "global/strich_icons/StrichIcons";



const PodProductSelector = ({

    // All the canonical strich products
    canonicalProducts,

    // Original design roles before the current edits
    originalRoles,

    // Current selection of data
    currentProducts,

    // Set the current selection of data
    setCurrentProducts,

    // Current set of roles - used with build mockup (will be generic placeholder image when creating template presets for POD Products) 
    currentRoles,

    // Basic role key (the preferred role usually front)
    basicDesignRoleKey,

    // All the roles in their original fetched order
    allRoles,

    // The selected roles the user wants to add a design to
    selectedRoles,

    // Print area roles - need this for doing a title lookup
    printAreaRoles,

    // Design or Template flag
    designOrTemplate,

    // Design details (title etc.)
    podDesignDetails,

    // Flag to show if we're adding a basic (front only) or advanced (mutiple roles) design set
    isAddingAdvancedDesignSet,

    // Passed in form values so we can access the latest design title
    formValues,

    // Flag whether the user is adding a new design
    isAddingNewDesign,

    // Function to save pending design-role changes before opening a product
    prepareDesignForProductOpen,

    // Whether main-page product controls are waiting to be saved
    productGridChangesPending,

    // Mark main-page product controls as changed
    markProductGridChangesPending,

    // Function to run the full design save after products have been added
    saveDesignAfterAddingProducts,

    // Commit one authoritative saved product family to current and original state
    onProductFamilyCommitted,

    // This is a stich template flag
    isStrichTemplate,

}) => {

    // Define toast options
    const { toastError } = useToast();

    // Store pop up state for show product details pop up
    const [productDetailsPopupIsOpen, setProductDetailsPopupIsOpen] = useState(false);

    // Store latest transform payload - from image placement editor
    const [storeHomeCountry, setStoreHomeCountry] = useState(null);

    // State for storing selected product sku - for canonical product lookup in the pop up details
    const [selectedProductSku, setSelectedProductSku] = useState(null);

    // State for storing selected configured product id - for editing the assigned design/template product instance
    const [selectedConfiguredProductId, setSelectedConfiguredProductId] = useState(null);

    // Store latest transform payload - from image placement editor
    const [latestTransform, setLatestTransform] = useState(null);

    // This stores the latest currentProducts without waiting for a new function closure
    const currentProductsRef = useRef(currentProducts);

    // Keep the ref in sync with the latest prop
    useEffect(() => {

        currentProductsRef.current = currentProducts;

    }, [currentProducts]);



    // Get the token from AuthContext
    const { token } = useContext(AuthContext);

    // Get the user data from user context
    const { connectionsData } = useContext(UserContext);






    // Get the store home country when connections data and form values load
    useEffect(() => {

        if (connectionsData && Array.isArray(connectionsData)) {

            // If there is a store id then use that to get that connection data
            if (formValues.store_id) {

                // Find the selected store connection
                const selectedConnection = connectionsData.find((con) => String(con.id) === String(formValues.store_id));

                // Find the home country setting's value
                const storeHomeCountry = selectedConnection?.country || "";

                // Set the state
                setStoreHomeCountry(storeHomeCountry);

            } else {

                // If there is no store id then this is a new unsaved design and the user hasn't selected a store yet so use the default (what they see in the dropdown)
                const strichConnections = connectionsData.filter((conn) => conn.conntype === "STRICH") || [];

                // From the strich only connections find the first one and use it's country setting
                const defaultConnectionStoreHomeCountry = strichConnections[0].country || "";

                // Set the state
                setStoreHomeCountry(defaultConnectionStoreHomeCountry);

            }

        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectionsData, formValues.store_id]);





    // Loading state
    const [loadingPodProductDetails, setLoadingPodProductDetails] = useState(null);

    // State for storing canonical pod product details
    const [podProductDetails, setPodProductDetails] = useState(null);

    // State for storing canonical pod product translations as per chosen language 
    const [podProductTranslations, setPodProductTranslations] = useState(null);

    // Store state of chosen language (English by default)
    const [chosenLanguage,] = useState("EN");

    // Set pop up status
    const [imageCarouselPopupOpen, setImageCarouselPopupOpen] = useState(false);

    // Store all mockup images - urls only
    const [allMockupImages, setAllMockupImages] = useState(null);

    // Store selected mockup image
    const [selectedMockupImage, setSelectedMockupImage] = useState(null);

    // Loading state for products active toggle
    const [loadingProductActiveToggle, setLoadingProductActiveToggle] = useState(null);

    // This stores the latest product details request id
    const latestProductDetailsRequestRef = useRef(null);

    // This stores the sku the user most recently clicked
    const latestRequestedProductSkuRef = useRef(null);

    // This stores a safe request id generator for product details fetches
    const buildProductDetailsRequestId = () => {

        // Return a reasonably unique request id
        return `product_details_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    };

    // Save the latest transform data to the parent or child product role
    const saveTransformDataToProductRole = (transformData) => {

        // Safe configured product id
        const safeConfiguredProductId = selectedConfiguredProductId ? String(selectedConfiguredProductId) : "";

        // Check the change source of incoming data - return early if it's initial
        if (transformData?.changeSource === "init") {

            // Nothing changed so return a no-op result
            return {
                didUpdate: false,
                nextProducts: null,
                target: null,
                roleDataToUpsert: null,
                savedTransform: {
                    ...(transformData ?? {}),
                    configuredProductId: safeConfiguredProductId,
                },
            };

        }

        // Set the role data payload that we want to update the product's role with 
        let roleDataToUpsert = null;

        // Check the change source of incoming data - return stripped payload if it's reset_to_default
        if (transformData?.changeSource === "reset_to_default") {

            roleDataToUpsert = {
                role: transformData.role,
                active: transformData.active,
                background_colour: transformData.edited_background === "Y" ? transformData.background_colour : null,
                position: transformData.edited_position === "Y" ? transformData.position : null,
                edited: transformData.edited_background === "Y" || transformData.edited_position === "Y" ? "Y" : "N",
            };

        }

        // Check the change source of incoming data - if it's user
        if (transformData?.changeSource === "user") {

            // If it has not been edited, keep only the minimal payload
            if (transformData?.edited === "N") {

                roleDataToUpsert = {
                    role: transformData.role,
                    active: transformData.active,
                    background_colour: transformData.edited_background === "Y" ? transformData.background_colour : null,
                    position: transformData.edited_position === "Y" ? transformData.position : null,
                    edited: transformData.edited_background === "Y" || transformData.edited_position === "Y" ? "Y" : "N",
                };

            }

            // If it has been edited, save the full placement payload
            if (transformData?.edited === "Y") {

                roleDataToUpsert = {
                    role: transformData.role,
                    active: transformData.active,
                    edited: "Y",
                    scale_x: transformData.scale_x,
                    scale_y: transformData.scale_y,
                    centre_x: transformData.centre_x,
                    centre_y: transformData.centre_y,
                    rotation: transformData.rotation,
                    fit_fill: transformData.fit_fill,
                    position: transformData.edited_position === "Y" ? transformData.position : null,
                    background_colour: transformData.edited_background === "Y" ? transformData.background_colour : null,
                };

            }

        }

        // Guard against missing payload
        if (!roleDataToUpsert?.role) {

            // Return a no-op result
            return {
                didUpdate: false,
                nextProducts: null,
                target: null,
                roleDataToUpsert: null,
                savedTransform: {
                    ...(transformData ?? {}),
                    configuredProductId: safeConfiguredProductId,
                },
            };

        }

        // Work out if this is a child or parent product
        const isChild = Boolean(transformData?.child_sku && transformData?.child_sku !== "");

        // Safe product sku
        const safeProductSku = String(transformData?.product_sku || "");

        // Safe child sku
        const safeChildSku = isChild ? String(transformData?.child_sku || "") : "";

        // Find the current product role data from latest podDesignDetails snapshot
        let currentRoleData = null;

        // Match the configured product by id or temporary client configured product id only
        const matchedProduct = podDesignDetails?.products?.find((p) => {

            // Use configured product id only
            if (safeConfiguredProductId) {
                return String(p?.id || "") === safeConfiguredProductId || String(p?.client_configured_product_id || "") === safeConfiguredProductId;
            }

            // No configured product id means no safe match
            return false;

        }) || null;

        const matchedChild = matchedProduct?.children?.find((c) => c?.pod_product_sku === transformData?.child_sku) || null;

        // Read current role data from child or parent
        if (matchedChild && isChild) {

            currentRoleData = matchedChild?.roles?.find((r) => r?.role === String(transformData?.role)) || null;

        } else {

            currentRoleData = matchedProduct?.roles?.find((r) => r?.role === String(transformData?.role)) || null;

        }

        // Helper to normalise role data for comparison
        const normaliseRoleData = (item) => {

            return {
                role: item?.role ?? null,
                active: item?.active ?? null,
                background_colour: item?.background_colour ?? null,
                position: item?.position ?? null,
                fit_fill: item?.fit_fill ?? null,
                scale_x: item?.scale_x ?? null,
                scale_y: item?.scale_y ?? null,
                centre_x: item?.centre_x ?? null,
                centre_y: item?.centre_y ?? null,
                rotation: item?.rotation ?? null,
                initial_view_rotation: item?.initial_view_rotation ?? null,
                view_settings: item?.view_settings ?? null,
            };

        };

        // Preserve the product view rotation used to establish this placement
        if (roleDataToUpsert) {
            roleDataToUpsert.initial_view_rotation = transformData?.initial_view_rotation ?? currentRoleData?.initial_view_rotation ?? 0;
        }

        // Preserve view settings when placement edits replace or merge role data
        if (roleDataToUpsert) {
            roleDataToUpsert.view_settings = currentRoleData?.view_settings ?? {
                aspects: ["S", "T", "W"],
                rotations: [0, 90, 180, 270],
            };
        }

        // Check if the payload is the same as current role data
        const isSameRoleData =
            JSON.stringify(normaliseRoleData(roleDataToUpsert)) ===
            JSON.stringify(normaliseRoleData(currentRoleData));

        // If unchanged, return early
        if (isSameRoleData) {

            return {
                didUpdate: false,
                nextProducts: Array.isArray(currentProductsRef.current) ? currentProductsRef.current : [],
                target: {
                    isChild: isChild,
                    configuredProductId: safeConfiguredProductId,
                    productSku: safeProductSku,
                    childSku: safeChildSku,
                    role: roleDataToUpsert.role,
                },
                roleDataToUpsert: roleDataToUpsert,
                savedTransform: {
                    ...(transformData ?? {}),
                    configuredProductId: safeConfiguredProductId,
                },
            };

        }

        // Check if this update should replace the existing role data instead of merging it
        const shouldReplaceRoleData = transformData?.changeSource === "reset_to_default";

        // This will capture the exact next products array used in the state update
        let capturedNextProducts = null;

        // Update the products using a functional state update so we can build the fresh nextProducts synchronously
        setCurrentProducts((prevDetails) => {

            // Ensure safe object
            const safePrevDetails = prevDetails ?? {};

            // Ensure products array
            const prevProducts = Array.isArray(safePrevDetails?.products) ? safePrevDetails.products : [];

            // Build next products immutably
            const nextProducts = prevProducts.map((productItem) => {

                    // If we have a configured product id, only update that exact configured product
                    if (safeConfiguredProductId && String(productItem?.id || "") !== safeConfiguredProductId && String(productItem?.client_configured_product_id || "") !== safeConfiguredProductId) {

                        return productItem;

                    }

                // If there is no configured product id, do not update by sku
                if (!safeConfiguredProductId) {

                    return productItem;

                }

                // If this is a child update, update the matching child only
                if (isChild) {

                    // Ensure child array
                    const safeChildren = Array.isArray(productItem?.children) ? productItem.children : [];

                    // Build next children
                    const nextChildren = safeChildren.map((childItem) => {

                        // Read child sku safely
                        const childSku = String(childItem?.pod_product_sku || "");

                        // Skip non-matching child
                        if (childSku !== safeChildSku) {

                            return childItem;

                        }

                        // Ensure roles array
                        const safeRoles = Array.isArray(childItem?.roles) ? childItem.roles : [];

                        // Find whether the role exists already
                        const roleExists = safeRoles.some((roleItem) => String(roleItem?.role || "") === String(roleDataToUpsert.role || ""));

                        // Build updated roles
                        const nextRoles = roleExists
                            ? safeRoles.map((roleItem) => {

                                if (String(roleItem?.role || "") !== String(roleDataToUpsert.role || "")) {

                                    return roleItem;

                                }

                                // If reset was clicked, replace the whole role object with the reset payload
                                if (shouldReplaceRoleData) {

                                    // This removes old placement values like scale, centre and rotate
                                    return roleDataToUpsert;

                                }

                                // Otherwise merge normal user edits into the existing role object
                                return {
                                    ...roleItem,
                                    ...roleDataToUpsert,
                                };

                            })
                            : [
                                ...safeRoles,
                                roleDataToUpsert,
                            ];

                        // Return updated child
                        return {
                            ...childItem,
                            roles: nextRoles,
                        };

                    });

                    // Return updated parent product
                    return {
                        ...productItem,
                        children: nextChildren,
                    };

                }

                // Parent update path
                const safeRoles = Array.isArray(productItem?.roles) ? productItem.roles : [];

                // Find whether the role exists already
                const roleExists = safeRoles.some((roleItem) => String(roleItem?.role || "") === String(roleDataToUpsert.role || ""));

                // Build updated roles
                const nextRoles = roleExists
                    ? safeRoles.map((roleItem) => {

                        if (String(roleItem?.role || "") !== String(roleDataToUpsert.role || "")) {

                            return roleItem;

                        }

                        // If reset was clicked, replace the whole role object with the reset payload
                        if (shouldReplaceRoleData) {

                            // This removes old placement values like scale, centre and rotate
                            return roleDataToUpsert;

                        }

                        // Otherwise merge normal user edits into the existing role object
                        return {
                            ...roleItem,
                            ...roleDataToUpsert,
                        };

                    })
                    : [
                        ...safeRoles,
                        roleDataToUpsert,
                    ];

                // Return updated parent product
                return {

                    // Keep the existing product data
                    ...productItem,

                    // Store the updated role data
                    roles: nextRoles,

                };

            });

            // Capture the exact next products array for the immediate save
            capturedNextProducts = nextProducts;

            // Keep the ref in sync immediately
            currentProductsRef.current = nextProducts;

            // Return next state
            return {
                ...safePrevDetails,
                products: nextProducts,
            };

        });

        // Return the exact updated products and target identity
        return {
            didUpdate: true,
            nextProducts: capturedNextProducts,
            target: {
                isChild: isChild,
                configuredProductId: safeConfiguredProductId,
                productSku: safeProductSku,
                childSku: safeChildSku,
                role: roleDataToUpsert.role,
            },
            roleDataToUpsert: roleDataToUpsert,
            savedTransform: {
                ...(transformData ?? {}),
                configuredProductId: safeConfiguredProductId,
            },
        };

    };


    // Check whether a design role has usable image data
    const designRoleHasImageData = (roleKey) => {

        // Find the matching design role
        const designRole = currentRoles.find((roleItem) => String(roleItem?.role || "") === String(roleKey || ""));

        // Return true if the role has usable image data
        return Boolean(
            designRole?.image_url ||
            designRole?.thumbnail_url ||
            designRole?.uploadedImage ||
            designRole?.imageDetails?.image_url ||
            designRole?.asset_id ||
            designRole?.image_id
        );

    };


    // Build selected roles with availability info
    const designSetRoleOptions = (selectedRoles || [])
        .filter((roleItem) => roleItem?.selected === true)
        .map((roleItem) => {

            // Get role key
            const roleKey = String(roleItem?.role || "");

            // Find display role
            const displayRole = printAreaRoles.find((printAreaRole) => printAreaRole.role === roleKey);

            // Return role option
            return {
                role: roleKey,
                name: displayRole?.name || roleKey,
                hasImageData: designRoleHasImageData(roleKey),
            };

        });


    // Build role keys that can actually be used for product matching
    const usableSelectedRoleKeys = new Set(
        designSetRoleOptions
            .filter((roleItem) => roleItem.hasImageData)
            .map((roleItem) => roleItem.role)
    );


    // Get matching usable role keys for a product family
    const getCanonicalProductFamilyMatchingUsableRoleKeys = (canonicalProduct) => {

        // Get supported role keys for this parent and its children
        const supportedRoleKeys = getCanonicalProductFamilySupportedRoleKeys(canonicalProduct);

        // Return usable selected roles that this family supports
        return Array.from(usableSelectedRoleKeys).filter((roleKey) => supportedRoleKeys.has(roleKey));

    };


    // This gets the design image aspect for a role
    const getDesignImageAspectForRole = (roleKey) => {

        // Templates should support every product view aspect
        if (designOrTemplate === "template") {
            return {
                success: true,
                results: "ALL",
            };
        }

        // Find the matching current design role
        const designRole = Array.isArray(currentRoles)
            ? currentRoles.find((roleItem) => String(roleItem?.role || "") === String(roleKey || ""))
            : null;

        // Read dimensions
        const designWidth = Number(designRole?.design_width || 0);
        const designHeight = Number(designRole?.design_height || 0);

        // Missing dimensions means the design role is broken
        if (!designWidth || !designHeight) {
            return {
                success: false,
                error_message: "Could not read design image dimensions for role: " + roleKey,
            };
        }

        // Tall design
        if (designHeight > designWidth) {
            return {
                success: true,
                results: "T",
            };
        }

        // Wide design
        if (designWidth > designHeight) {
            return {
                success: true,
                results: "W",
            };
        }

        // Square design
        return {
            success: true,
            results: "S",
        };

    };



    // This builds default role view settings from the design image aspect
    const buildDefaultViewSettingsForRole = (roleKey) => {

        // Get the design image aspect
        const aspectResults = getDesignImageAspectForRole(roleKey);

        // Return early if it fails
        if (!aspectResults.success) { return aspectResults; }

        // Templates should default to all product view aspects
        if (aspectResults.results === "ALL") {
            return {
                success: true,
                results: {
                    aspects: ["S", "T", "W"],
                    rotations: [0, 90, 180, 270],
                },
            };
        }

        // Tall designs should default to tall product views
        if (aspectResults.results === "T") {
            return {
                success: true,
                results: {
                    aspects: ["T"],
                    rotations: [0, 90, 180, 270],
                },
            };
        }

        // Wide designs should default to wide product views
        if (aspectResults.results === "W") {
            return {
                success: true,
                results: {
                    aspects: ["W"],
                    rotations: [0, 90, 180, 270],
                },
            };
        }

        // Square designs should default to square product views
        return {
            success: true,
            results: {
                aspects: ["S"],
                rotations: [0, 90, 180, 270],
            },
        };

    };



    // This gets the supported role keys for a canonical POD product
    const getSupportedRoleKeysForCanonicalProduct = (canonicalProduct) => {

        // Return supported role keys from parent and children
        return getCanonicalProductFamilySupportedRoleKeys(canonicalProduct);

    };



    // This builds default configured role rows for a canonical POD product
    const buildDefaultConfiguredRolesForCanonicalProduct = (canonicalProduct) => {

        // Get selected roles
        const safeSelectedRoles = Array.isArray(selectedRoles) ? selectedRoles : [];

        // Get supported role keys
        const supportedRoleKeys = getSupportedRoleKeysForCanonicalProduct(canonicalProduct);

        // Build role rows
        const configuredRoles = [];

        // Go through selected roles
        for (const selectedRole of safeSelectedRoles) {

            // Skip unselected roles
            if (selectedRole?.selected !== true) { continue; }

            // Get role key
            const roleKey = String(selectedRole?.role || "");

            // Skip roles this canonical product does not support
            if (!supportedRoleKeys.has(roleKey)) { continue; }

            // Start without default view settings
            let viewSettingsResults = null;

            // Use image-specific aspect settings when this role currently has an image
            if (designRoleHasImageData(roleKey)) {

                viewSettingsResults =
                    buildDefaultViewSettingsForRole(roleKey);

            } else {

                // Empty selected roles must still exist and allow any future image aspect
                viewSettingsResults = {
                    success: true,
                    results: {
                        aspects: ["S", "T", "W"],
                        rotations: [0, 90, 180, 270],
                    },
                };

            }

            // Stop when valid role view settings could not be built
            if (!viewSettingsResults.success) {
                return viewSettingsResults;
            }

            // Add configured role row
            configuredRoles.push({
                role: roleKey,
                active: "Y",
                edited: "N",
                view_settings: viewSettingsResults.results,
            });

        }

        // Return configured roles
        return {
            success: true,
            results: configuredRoles,
        };

    };



    // Build default mockup image rows for a canonical POD product
    const buildDefaultMockupImagesForCanonicalProduct = (canonicalProduct, configuredRoles) => {

        // Read configured roles safely
        let safeConfiguredRoles = [];

        if (Array.isArray(configuredRoles)) {
            safeConfiguredRoles = configuredRoles;
        }

        // Only roles with actual preview/design images can choose generated mockups
        const configuredRolesWithImages = safeConfiguredRoles.filter((configuredRole) => {

            // Read the configured role key
            const roleKey = String(configuredRole?.role || "");

            // Keep only roles which currently have usable image data
            return designRoleHasImageData(roleKey);

        });

        // Do not choose generated mockups until at least one configured role has an image
        if (configuredRolesWithImages.length < 1) {
            return [];
        }

        // Start with no default configurations
        let defaultConfigurations = [];

        if (designOrTemplate === "template") {

            // Templates should start with every mockup configuration matching their configured roles
            defaultConfigurations =
                getAvailableMockupConfigurationsForConfiguredRoles(
                    canonicalProduct,
                    configuredRolesWithImages
                );

        } else {

            // Normal designs only need one preferred configuration for each usable print area
            defaultConfigurations =
                getPreferredMockupConfigurationsForConfiguredRoles(
                    canonicalProduct,
                    configuredRolesWithImages
                );

        }

        // If there are no valid exact configurations, return an empty image list
        if (defaultConfigurations.length < 1) {
            return [];
        }

        // Turn every selected exact configuration into a pending mockup row
        return defaultConfigurations.map(
            (defaultConfiguration, configurationIndex) => {

                return {
                    mockup_id:
                        defaultConfiguration.mockup_id,
                    aspect:
                        defaultConfiguration.aspect,
                    rotation:
                        defaultConfiguration.rotation,
                    ordering:
                        configurationIndex + 1,
                };

            }
        );

    };



    // Build a new unsaved configured product from a canonical POD product
    const buildNewConfiguredProductFromCanonicalProduct = (canonicalProduct) => {

        // This stores the canonical child products for this parent
        const safeCanonicalChildren = Array.isArray(canonicalProduct?.children) ? canonicalProduct.children : [];

        // Get parent colourways and the colourway lists for every colourway-capable child
        const parentAvailableColourwayCodes = getAvailableProductColourwayCodes(canonicalProduct);
        const childAvailableColourwayCodeLists = safeCanonicalChildren
            .map((canonicalChild) => getAvailableProductColourwayCodes(canonicalChild))
            .filter((colourwayCodes) => colourwayCodes.length > 0);

        // Find parent defaults available on every colourway-capable child
        const commonDefaultColourwayCodes = parentAvailableColourwayCodes.filter((colourwayCode) => {
            return childAvailableColourwayCodeLists.every((childColourwayCodes) => childColourwayCodes.includes(colourwayCode));
        });

        // A colourway-capable family requires at least one usable parent default
        if (parentAvailableColourwayCodes.length > 0 && childAvailableColourwayCodeLists.length > 0 && commonDefaultColourwayCodes.length === 0) {
            toastError({ title: "Error", message: "This product cannot be added because it has no default colourway available on every child product.", autoCloseMs: 6000 });
            return null;
        }

        // Build the parent colourway state once so colourway-capable children can inherit it
        const parentColourwaySelection = buildDefaultColourwaySelection(canonicalProduct, {
            defaultColourwayCode: commonDefaultColourwayCodes[0] || parentAvailableColourwayCodes[0] || "",
            defaultColourwayInherited: "N",
        });

        // Build default parent roles from the design image aspect
        const parentRoleResults = buildDefaultConfiguredRolesForCanonicalProduct(canonicalProduct);
        if (!parentRoleResults.success) {

            // Show error
            toastError({ title: "Error", message: parentRoleResults.error_message, autoCloseMs: 6000 });

            // Stop product creation
            return null;

        }

        // Build child products
        const childProducts = [];

        // Go through canonical children
        for (const canonicalChild of safeCanonicalChildren) {

            // Build default child roles from the design image aspect
            const childRoleResults = buildDefaultConfiguredRolesForCanonicalProduct(canonicalChild);
            if (!childRoleResults.success) {

                // Show error
                toastError({ title: "Error", message: childRoleResults.error_message, autoCloseMs: 6000 });

                // Stop product creation
                return null;

            }

            // Build the child default mockup image rows from its configured roles
            const childDefaultImages = buildDefaultMockupImagesForCanonicalProduct(canonicalChild, childRoleResults.results);

            // Stop when the child has no compatible mockup
            if (childDefaultImages.length < 1) {

                // Show a clear configuration error
                toastError({ title: "Error", message: "This product cannot be added because child product " + String(canonicalChild?.sku || "") + " has no mockups compatible with its selected role aspects and rotations.", autoCloseMs: 6000 });

                // Stop product creation
                return null;

            }

            // Build the child product
            const childProduct = {

                // Store a temporary client-side configured child product id for unsaved duplicate products
                client_configured_product_id: `new_child_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,

                // Store a temporary client-side created time so new unsaved child products can be mapped safely
                client_created_at: Date.now(),

                // Store child sku consistently
                pod_product_sku: canonicalChild?.sku ?? null,

                // Default values
                active: "Y",

                // Add all canonical child colourways and inherit the configured parent default when available
                ...buildDefaultColourwaySelection(canonicalChild, {
                    defaultColourwayCode: parentColourwaySelection.default_colourway || "",
                    defaultColourwayInherited: parentColourwaySelection.default_colourway ? "Y" : "N",
                }),

                // Add default role view settings
                roles: childRoleResults.results,

                // Add the first compatible canonical mockup as a pending image row
                images: childDefaultImages,

            };

            // Add valid child product
            if (childProduct?.pod_product_sku) {
                childProducts.push(childProduct);
            }

        }

        // Build the parent default mockup image rows from its configured roles
        const parentDefaultImages = buildDefaultMockupImagesForCanonicalProduct(canonicalProduct, parentRoleResults.results);

        // Stop when the parent has no compatible mockup
        if (parentDefaultImages.length < 1) {

            // Show a clear configuration error
            toastError({ title: "Error", message: "This product cannot be added because it has no mockups compatible with its selected role aspects and rotations.", autoCloseMs: 6000 });

            // Stop product creation
            return null;

        }

        // Build the new configured product
        return {

            // Store a temporary client-side configured product id for unsaved duplicate products
            client_configured_product_id: `new_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,

            // Store a temporary client-side created time so new unsaved products sort newest first
            client_created_at: Date.now(),

            // Store sku consistently
            pod_product_sku: canonicalProduct?.sku ?? null,

            // Default values
            active: "Y",

            // Add all canonical colourways using a default available on every colourway-capable child
            ...parentColourwaySelection,

            // Add default role view settings
            roles: parentRoleResults.results,

            // Add the first compatible canonical mockup as a pending image row
            images: parentDefaultImages,

            // Seed children so child mockups can also be generated
            children: childProducts,

        };

    };

    // Function to get product details when clicking on a product in the selector
    const handleGetPodProductDetails = async (sku, configuredProductId = null) => {

        // Save pending design-role changes before showing product-specific data
        if (typeof prepareDesignForProductOpen === "function") {

            const productCanOpen =
                await prepareDesignForProductOpen(
                    configuredProductId
                );

            // Stay on the design when the save is cancelled or fails
            if (!productCanOpen) {
                return;
            }

        }

        // Build a unique request id for this fetch
        const requestId = buildProductDetailsRequestId();

        // Store this request as the latest one
        latestProductDetailsRequestRef.current = requestId;

        // Store the sku the user most recently asked for
        latestRequestedProductSkuRef.current = String(sku || "");

        // Set selected product immediately so the UI knows what canonical product the user most recently clicked
        setSelectedProductSku(sku);

        // Set selected configured product id so edits target the product instance, not just the canonical sku
        setSelectedConfiguredProductId(configuredProductId);

        // Clear old popup data so stale content cannot flash while loading the new product
        setPodProductDetails(null);

        // Clear old translations
        setPodProductTranslations(null);

        // Close the popup until the latest request finishes successfully
        setProductDetailsPopupIsOpen(false);

        // Set loading
        setLoadingPodProductDetails(true);

        // Get the pod product prices from the store home country
        const storeCountryPricesResponse = await getPodProductPrices(token, storeHomeCountry);

        try {

            // Make the call to get the canonical product details
            const response = await getPodProduct(token, sku, designOrTemplate === "design" ? "Y" : "N", storeHomeCountry);

            // If a newer request started while this one was in flight, ignore this response completely
            if (latestProductDetailsRequestRef.current !== requestId) {

                // Exit early because this response is stale
                return;

            }

            // If the response failed, stop here
            if (!response?.success) {

                // Log the failure
                console.log("Error getting pod product details.");

                // Exit early
                return;

            }

            // Safely read canonical product
            const canonicalProduct = response?.results || null;

            // Guard against missing canonical product
            if (!canonicalProduct?.sku) {

                // Exit early
                return;

            }

            // Extra safety check - only continue if the returned product still matches the latest clicked sku
            if (String(canonicalProduct.sku) !== String(latestRequestedProductSkuRef.current || "")) {

                // Exit early because the user has already clicked a different product
                return;

            }

            // Find the matching pod product in the prices response so we can add it to the product details - quick and easy way of getting the home country default price
            const matchedProductPrice = storeCountryPricesResponse?.results?.find((p) => p?.sku === String(sku || "")) || [];

            // Merge with the canonical product details
            const canonicalProductDetailsWithHomeCountryPricing = { ...canonicalProduct, homeCountryPricing: matchedProductPrice };

            // Set canonical product data
            setPodProductDetails(canonicalProductDetailsWithHomeCountryPricing);

            // Set the translations for the chosen language
            setPodProductTranslations(
                canonicalProduct?.venues?.[0]?.translations?.find((trans) => trans?.language === chosenLanguage) || null
            );

            // Reconfirm the selected sku from the successful latest request
            setSelectedProductSku(canonicalProduct.sku);

            // Reconfirm the selected configured product id from the current click
            setSelectedConfiguredProductId(configuredProductId);

            // Open the popup only for the latest valid request
            setProductDetailsPopupIsOpen(true);

            // Optional: if you want to eagerly generate parent mockups when missing, keep that logic here
            // but only after all the stale-request guards above

        } catch (error) {

            // Only log the error if this request is still the latest one
            if (latestProductDetailsRequestRef.current === requestId) {

                // Log the error
                console.error("Failed to fetch pod product details:", error);

            }

        } finally {

            // Only clear loading if this request is still the latest one
            if (latestProductDetailsRequestRef.current === requestId) {

                // Clear loading
                setLoadingPodProductDetails(false);

            }

        }

    };



    // Handle opening the pop up with carousel of images 
    function handleCarouselLargeImages(mockup, mockupImages) {

        // Set all images state with all th emockup urls
        setAllMockupImages(mockupImages);

        // Set the selected mockup image (the one clicked) - to show as the first image in the pop up
        setSelectedMockupImage(mockup.url);

        // Open the pop up
        setImageCarouselPopupOpen(true);

    }



    // Build an unedited configured role while preserving settings which are not placement overrides
    const buildResetConfiguredRole = (roleItem) => {

        // Return only the fields which an unedited configured role needs
        return {

            // Keep the role key
            role: String(roleItem?.role || ""),

            // Keep the active state
            active: String(roleItem?.active ?? "Y"),

            // Keep the configured view restrictions
            view_settings: roleItem?.view_settings ?? {
                aspects: ["S", "T", "W"],
                rotations: [0, 90, 180, 270],
            },

        };

    };


    // Remove all edited placement overrides from one configured parent product and its children
    const buildResetConfiguredProductFamily = (product) => {

        // Read parent roles safely
        const parentRoles = Array.isArray(product?.roles) ? product.roles : [];

        // Read configured children safely
        const children = Array.isArray(product?.children) ? product.children : [];

        // Reset every child role
        const nextChildren = children.map((child) => {

            // Read child roles safely
            const childRoles = Array.isArray(child?.roles) ? child.roles : [];

            // Return the child without placement overrides
            return {
                ...child,
                roles: childRoles.map((roleItem) => buildResetConfiguredRole(roleItem)),
            };

        });

        // Return the complete reset family
        return {
            ...product,
            roles: parentRoles.map((roleItem) => buildResetConfiguredRole(roleItem)),
            children: nextChildren,
        };

    };


    // Reset one configured product family from the main design screen
    const handleClearParentAndChildProductRoleData = ({ configuredProduct, configuredProductIndex, canonicalProduct }) => {

        // Apply the reset after confirmation
        const clearProductData = () => {

            // Clear any stale placement-editor transform
            setLatestTransform(null);

            // Update only this configured product family
            setCurrentProducts((prevDetails) => {

                // Read the complete design safely
                const safePrevDetails = prevDetails ?? {};

                // Read configured products safely
                const prevProducts = Array.isArray(safePrevDetails?.products) ? safePrevDetails.products : [];

                // Read the configured identities used to find this exact product
                const configuredProductId = String(configuredProduct?.id || "");
                const clientConfiguredProductId = String(configuredProduct?.client_configured_product_id || "");

                // Reset only the requested product family
                const nextProducts = prevProducts.map((productItem, productIndex) => {

                    // Start without a match
                    let productMatches = false;

                    // Match existing configured products by their real id
                    if (configuredProductId) {
                        productMatches = String(productItem?.id || "") === configuredProductId;
                    }

                    // Match unsaved configured products by their temporary id
                    if (!configuredProductId && clientConfiguredProductId) {
                        productMatches = String(productItem?.client_configured_product_id || "") === clientConfiguredProductId;
                    }

                    // Fall back to the original array position for an unsaved row without an id
                    if (!configuredProductId && !clientConfiguredProductId && productIndex === configuredProductIndex) {
                        productMatches = true;
                    }

                    // Keep unrelated products unchanged
                    if (!productMatches) { return productItem; }

                    // Remove the placement overrides from this complete family
                    return buildResetConfiguredProductFamily(productItem);

                });

                // Keep the synchronous product reference aligned immediately
                currentProductsRef.current = nextProducts;

                // Return the updated design
                return {
                    ...safePrevDetails,
                    products: nextProducts,
                };

            });

            // This reset belongs to the normal design-page Save button
            markProductGridChangesPending?.();

        };

        // Build the product name for the confirmation message
        let productTitle = "this product";

        if (canonicalProduct?.Title) {
            productTitle = canonicalProduct.Title;
        }

        // Ask before removing the product-family placement overrides
        DisplayConfirmationDialog(
            `<h3>Reset ${productTitle}?</h3><br/>This will clear all edited image placement data for this product and all of its child products.<br/><br/>The change will be saved when you save the design.`,
            "Reset Product",
            "Cancel",
            async function (is_confirmed) {

                // Leave everything unchanged when cancelled
                if (!is_confirmed) { return; }

                // Apply the reset
                clearProductData();

            }
        );

    };


    // Function to clear all the product role data for every configured product family
    const handleClearAllParentAndChildProductsRoleData = (showConfirmationDialog = true) => {

        // Clear every configured product family
        const clearAllProductData = () => {

            // Clear any stale placement-editor transform
            setLatestTransform(null);

            // Update all products in one state update
            setCurrentProducts((prevDetails) => {

                // Read the complete design safely
                const safePrevDetails = prevDetails ?? {};

                // Read configured products safely
                const prevProducts = Array.isArray(safePrevDetails?.products) ? safePrevDetails.products : [];

                // Remove placement overrides from every parent and child
                const nextProducts = prevProducts.map((product) => buildResetConfiguredProductFamily(product));

                // Keep the synchronous product reference aligned immediately
                currentProductsRef.current = nextProducts;

                // Return the updated design
                return {
                    ...safePrevDetails,
                    products: nextProducts,
                };

            });

            // The reset belongs to the main design-page Save button
            markProductGridChangesPending?.();

        };

        // Show the existing confirmation dialog when requested
        if (showConfirmationDialog) {

            DisplayConfirmationDialog(
                `<h3>Are you sure?</h3><br/>This will clear all edited image placement data on parent and child products.<br/><br/><span class='warning-message'>WARNING: This cannot be undone after saving.</span>`,
                "Clear All",
                "Cancel",
                async function (is_confirmed) {

                    // Leave everything unchanged when cancelled
                    if (!is_confirmed) { return; }

                    // Apply the reset
                    clearAllProductData();

                }
            );

            return;

        }

        // Apply without confirmation when explicitly requested
        clearAllProductData();

    };

    // Export the reusable actions bar component
    const RenderProductGridActionsBar = ({

        // Fires when the clear all edited products button is clicked
        handleClearAllParentAndChildProductsRoleData,

        // Optional loading/disabled state
        disabled = false,

        // Flag to check if any products have edited role data
        someProductRolesHaveBeenEdited = false,

    }) => {

        // Return the toolbar UI
        return (

            // Outer wrapper for the full actions bar
            <div className="productGridActionsBar">

                {/* Right side destructive/reset action */}
                <div className="productGridActionsBar__group productGridActionsBar__group--right">

                    {someProductRolesHaveBeenEdited &&

                        // Clear edited products button 
                        <button
                            className="productGridActionsBar__button productGridActionsBar__button--clear"
                            type="button"
                            onClick={handleClearAllParentAndChildProductsRoleData}
                            disabled={disabled}
                        >
                            Reset All Edited Products
                        </button>

                    }

                </div>

            </div>

        );

    };

    // This ref stores a lookup object of DOM nodes by item id so we can scroll to a specific item later
    const itemRefs = useRef({});

    // This function returns a ref-callback that React will call with the DOM node for a given item id
    const setItemRef = (sku) => (node) => {

        // This ensures we only store the ref when the DOM node actually exists (React can call with null on unmount)
        if (node) {

            // This saves the DOM node into our lookup so we can access it via itemRefs.current[id]
            itemRefs.current[sku] = node;

        }

    };



    // Function to toggle the active state of a role on a configured product assigned to a design
    const toggleDesignProductRoleActiveSate = (activeBoolean, role, currentProduct) => {

        // This stores the configured product identity safely
        const configuredProductId = String(currentProduct?.id || currentProduct?.client_configured_product_id || "");

        // This prevents unsafe updates if there is no configured product identity
        if (!configuredProductId) { return; }

        // This guarantees a nextChecked value even if the component gives us nothing useful
        const nextChecked = activeBoolean || false;

        // This stores the role key safely
        const roleKey = String(role?.role || "");

        // This prevents updates if role is missing
        if (!roleKey) { return; }

        // This updates current products safely
        setCurrentProducts((prevDetails) => {

            // This ensures products is always an array
            const prevProducts = Array.isArray(prevDetails?.products) ? prevDetails.products : [];

            // Build the next product array
            const nextProducts = prevProducts.map((productItem) => {

                // If this is not the exact configured product, keep it unchanged
                if (String(productItem?.id || "") !== configuredProductId && String(productItem?.client_configured_product_id || "") !== configuredProductId) {
                    return productItem;
                }

                // This ensures roles is always an array on the product
                const existingRoles = Array.isArray(productItem?.roles) ? productItem.roles : [];

                // This checks if the role already exists
                const roleExists = existingRoles.some((roleItem) => String(roleItem?.role || "") === roleKey);

                // Build the next roles array
                const nextRoles = roleExists
                    ? existingRoles.map((roleItem) => {

                        // Keep non-matching roles unchanged
                        if (String(roleItem?.role || "") !== roleKey) {
                            return roleItem;
                        }

                        // Return the updated role
                        return {

                            // Keep role fields
                            ...roleItem,

                            // Ensure role key
                            role: roleKey,

                            // Set active state
                            active: nextChecked ? "Y" : "N",

                        };

                    })
                    : [

                        // Keep existing roles
                        ...existingRoles,

                        // Add the role with its chosen active state
                        {

                            // Store role key
                            role: roleKey,

                            // Set active state
                            active: nextChecked ? "Y" : "N",

                            // Set position
                            position: null,

                        },

                    ];

                // Return updated configured product
                return {

                    // Keep product fields
                    ...productItem,

                    // Update roles
                    roles: nextRoles,

                };

            });

            // Return the next state
            return {

                // Keep other fields
                ...prevDetails,

                // Update products
                products: nextProducts,

            };

        });

        // The role toggle belongs to the main design-page Save button
        markProductGridChangesPending?.();

    };



    // Flag to check if we should show the 'reset position data' button for all products - // If any of the products (parents or child) have role data that has been edited 
    const someProductRolesHaveBeenEdited = (currentProducts?.some((prod) => prod.roles?.some((r) => r.edited === "Y")) || currentProducts?.some((prod) => prod.children?.some((child) => child.roles?.some((r) => r.edited === "Y"))));


    // State for showing the add product picker
    const [addProductPickerIsOpen, setAddProductPickerIsOpen] = useState(false);

    // Store the current visual state for each product card in the picker
    const [addProductPickerProductStates, setAddProductPickerProductStates] = useState({});

    // Keep the latest product card states available between rapid clicks
    const addProductPickerProductStatesRef = useRef({});

    // Count product additions which are still waiting for an API response
    const [addProductPickerPendingCount, setAddProductPickerPendingCount] = useState(0);

    // Keep the latest pending count available before React rerenders
    const addProductPickerPendingCountRef = useRef(0);

    // Track the final product refresh while the picker is closing
    const [addProductPickerIsClosing, setAddProductPickerIsClosing] = useState(false);

    // Keep the closing lock available before React rerenders
    const addProductPickerIsClosingRef = useRef(false);

    // Track whether products have been added during the current picker session
    const addProductPickerHasChangesRef = useRef(false);

    // Retain newly-added products with their local role and pending mockup data
    const addedProductsPendingSaveRef = useRef([]);

    // Store the status-reset timers so repeated additions of the same product remain predictable
    const addProductPickerStatusTimeoutsRef = useRef({});

    // Merge saved API rows with local fields which are persisted through dedicated endpoints
    const mergeSavedProductsWithLocalPendingData = (savedProducts, localProducts) => {

        // Read saved parent products safely
        let safeSavedProducts = [];

        // Keep the saved parent rows when they are valid
        if (Array.isArray(savedProducts)) {
            safeSavedProducts = savedProducts;
        }

        // Read complete local parent products safely
        let safeLocalProducts = [];

        // Keep the complete local parent rows when they are valid
        if (Array.isArray(localProducts)) {
            safeLocalProducts = localProducts;
        }

        // Find a matching local row by configured id first, then by temporary portal id
        const findMatchingLocalRow = (savedRow, localRows) => {

            // Start without a matching local row
            let matchingLocalRow = null;

            // Match existing configured rows by their real id
            if (savedRow?.id) {
                matchingLocalRow = localRows.find((localRow) => {
                    return String(localRow?.id || "") === String(savedRow.id);
                }) || null;
            }

            // Match newly-created rows by their temporary portal id
            if (!matchingLocalRow && savedRow?.client_configured_product_id) {
                matchingLocalRow = localRows.find((localRow) => {
                    return String(localRow?.client_configured_product_id || "") === String(savedRow.client_configured_product_id);
                }) || null;
            }

            // Return the matching complete local row
            return matchingLocalRow;

        };

        // Preserve local-only fields on a saved row
        const preserveLocalPendingFields = (savedRow, localRow) => {

            // Start with the saved API row so calculated values remain authoritative
            const mergedRow = {
                ...localRow,
                ...savedRow,
                id: savedRow.id,
            };

            // Preserve product role rows for the dedicated roles save
            if (localRow.roles !== undefined) {
                mergedRow.roles = localRow.roles;
            }

            // Preserve pending mockup rows for the dedicated images save
            if (localRow.images !== undefined) {
                mergedRow.images = localRow.images;
            }

            // Preserve the temporary mapping id until the complete save has finished
            if (localRow.client_configured_product_id !== undefined) {
                mergedRow.client_configured_product_id = localRow.client_configured_product_id;
            }

            // Preserve client-side sort data until the complete save has finished
            if (localRow.client_created_at !== undefined) {
                mergedRow.client_created_at = localRow.client_created_at;
            }

            // Preserve canonical product data used by the portal UI
            if (localRow.pod_product !== undefined) {
                mergedRow.pod_product = localRow.pod_product;
            }

            // Return the merged row
            return mergedRow;

        };

        // Merge each saved parent product with its complete local product
        return safeSavedProducts.map((savedProduct) => {

            // Find the matching complete local parent product
            const localProduct = findMatchingLocalRow(savedProduct, safeLocalProducts);

            // Keep the saved parent unchanged when there is no local match
            if (!localProduct) { return savedProduct; }

            // Read saved children safely
            let savedChildren = [];

            // Keep saved children when they are valid
            if (Array.isArray(savedProduct?.children)) {
                savedChildren = savedProduct.children;
            }

            // Read local children safely
            let localChildren = [];

            // Keep complete local children when they are valid
            if (Array.isArray(localProduct?.children)) {
                localChildren = localProduct.children;
            }

            // Merge each saved child with its complete local child row
            const mergedChildren = savedChildren.map((savedChild) => {

                // Find the matching complete local child product
                const localChild = findMatchingLocalRow(savedChild, localChildren);

                // Keep the saved child unchanged when there is no local match
                if (!localChild) { return savedChild; }

                // Preserve its pending image and role data
                return preserveLocalPendingFields(savedChild, localChild);

            });

            // Merge the parent product
            const mergedProduct = preserveLocalPendingFields(savedProduct, localProduct);

            // Add the merged children
            mergedProduct.children = mergedChildren;

            // Return the complete saved parent product
            return mergedProduct;

        });

    };

    // Set or clear one product card's visual state
    const setAddProductPickerProductState = (productSku, productState) => {

        // Normalise the product sku used as the state key
        const safeProductSku = String(productSku || "");

        // Stop when the product cannot be identified
        if (!safeProductSku) { return; }

        // Copy the latest state map
        const nextProductStates = {
            ...addProductPickerProductStatesRef.current,
        };

        // Store or clear this card state
        if (productState) {
            nextProductStates[safeProductSku] = productState;
        } else {
            delete nextProductStates[safeProductSku];
        }

        // Keep the synchronous ref and rendered state aligned
        addProductPickerProductStatesRef.current = nextProductStates;
        setAddProductPickerProductStates(nextProductStates);

    };

    // Open the add product picker with a clean change-tracking state
    const openAddProductPicker = () => {

        // Clear any outstanding visual-state timers from the previous picker session
        Object.values(addProductPickerStatusTimeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
        addProductPickerStatusTimeoutsRef.current = {};

        // Reset the change flag for this picker session
        addProductPickerHasChangesRef.current = false;

        // Clear products retained from the previous picker session
        addedProductsPendingSaveRef.current = [];

        // Clear all product card states
        addProductPickerProductStatesRef.current = {};
        setAddProductPickerProductStates({});

        // Reset request tracking
        addProductPickerPendingCountRef.current = 0;
        setAddProductPickerPendingCount(0);
        addProductPickerIsClosingRef.current = false;
        setAddProductPickerIsClosing(false);

        // Open the picker
        setAddProductPickerIsOpen(true);

    };

    // Save main-page product controls before opening the add product picker
    const handleOpenAddProductPicker = () => {

        // Open immediately when the main product grid has no pending changes
        if (!productGridChangesPending) {

            openAddProductPicker();

            return;

        }

        // Use the latest live products for the explicit save
        let latestProducts = [];

        if (
            Array.isArray(
                currentProductsRef.current
            )
        ) {
            latestProducts =
                currentProductsRef.current;
        }

        // Ask the user to save controls changed on the design page
        DisplayConfirmationDialog(
            `<h3>Unsaved product changes</h3><br/>You have unsaved product controls on this design page.<br/><br/>Save those changes before adding more products.`,
            "Save and Continue",
            "Cancel",
            async function (is_confirmed) {

                // Leave the current edits untouched when cancelled
                if (!is_confirmed) {
                    return;
                }

                // Stop if the save callback is unavailable
                if (
                    typeof saveDesignAfterAddingProducts !==
                    "function"
                ) {

                    toastError({
                        title: "Error",
                        message:
                            "Could not save the current product changes.",
                        autoCloseMs: 6000,
                    });

                    return;

                }

                // Save the explicit main-page product changes
                const saveResponse =
                    await saveDesignAfterAddingProducts(
                        latestProducts
                    );

                // Do not open the picker when saving failed
                if (!saveResponse?.success) {
                    return;
                }

                // Open with the product hierarchy now saved
                openAddProductPicker();

            }
        );

    };




    // Add one canonical product without blocking other product cards
    const handleAddProductFromPicker = async (canonicalProduct) => {

        // Read the canonical sku used to identify this card
        const canonicalProductSku = String(canonicalProduct?.sku || "");

        // Stop when the product cannot be identified
        if (!canonicalProductSku) { return; }

        // Stop duplicate clicks while this product card is already processing
        if (addProductPickerProductStatesRef.current[canonicalProductSku]) { return; }

        // Stop new additions once the picker has started closing
        if (addProductPickerIsClosingRef.current) { return; }

        // Build a new configured product from the canonical product
        const newConfiguredProduct = buildNewConfiguredProductFromCanonicalProduct(canonicalProduct);

        // Do nothing if the canonical product is invalid
        if (!newConfiguredProduct?.pod_product_sku) { return; }

        // Show the loading state and lock this card only
        setAddProductPickerProductState(canonicalProductSku, "adding");

        // Increment the synchronous and rendered pending counts
        addProductPickerPendingCountRef.current += 1;
        setAddProductPickerPendingCount(addProductPickerPendingCountRef.current);

        try {

            // Save only this newly-created product family
            const createProductResponse = await updatePodDesignProducts(
                token,
                designOrTemplate,
                podDesignDetails.id,
                [newConfiguredProduct]
            );

            // Stop if the product could not be created
            if (!createProductResponse?.success) {

                // Build a useful API error message
                let errorMessage = "Oops...something went wrong.";

                if (createProductResponse?.error_message) {
                    errorMessage = createProductResponse.error_message;
                }

                // Show the API error
                toastError({
                    title: "Error",
                    message: "Could not add new product: " + errorMessage,
                    autoCloseMs: 6000
                });

                // Restore the card immediately
                setAddProductPickerProductState(canonicalProductSku, null);

                return;

            }

            // Read the saved product rows safely
            let savedProducts = [];

            if (Array.isArray(createProductResponse?.results)) {
                savedProducts = createProductResponse.results;
            }

            // Merge the saved ids and calculated fields into this complete local product family
            const savedProductFamily = mergeSavedProductsWithLocalPendingData(
                savedProducts,
                [newConfiguredProduct]
            );

            // Read the temporary id for the product added by this click
            const productClientConfiguredProductId = String(
                newConfiguredProduct?.client_configured_product_id || ""
            );

            // Find the complete newly-created parent product after its real id was merged in
            const savedNewConfiguredProduct = savedProductFamily.find((product) => {

                return String(product?.client_configured_product_id || "") === productClientConfiguredProductId;

            });

            // Stop if the API did not return the new configured product id
            if (!savedNewConfiguredProduct?.id) {

                toastError({
                    title: "Error",
                    message: "The product was added, but the API did not return its configured product id.",
                    autoCloseMs: 6000
                });

                setAddProductPickerProductState(canonicalProductSku, null);

                return;

            }

            // Read the newly-created local children safely
            let localChildren = [];

            if (Array.isArray(newConfiguredProduct?.children)) {
                localChildren = newConfiguredProduct.children;
            }

            // Read the newly-created saved children safely
            let savedChildren = [];

            if (Array.isArray(savedNewConfiguredProduct?.children)) {
                savedChildren = savedNewConfiguredProduct.children;
            }

            // Check that every newly-created child received a real configured id
            for (const localChild of localChildren) {

                // Read the temporary child product id
                const childClientConfiguredProductId = String(
                    localChild?.client_configured_product_id || ""
                );

                // Find the matching saved child
                const savedChild = savedChildren.find((child) => {

                    return String(child?.client_configured_product_id || "") === childClientConfiguredProductId;

                });

                // Stop if the API did not return this child configured product id
                if (!savedChild?.id) {

                    toastError({
                        title: "Error",
                        message: "The product was added, but the API did not return one of its configured child product ids.",
                        autoCloseMs: 6000
                    });

                    setAddProductPickerProductState(canonicalProductSku, null);

                    return;

                }

            }

            // Retain this complete saved product for the final roles and mockup save
            addedProductsPendingSaveRef.current = [
                ...addedProductsPendingSaveRef.current,
                savedNewConfiguredProduct,
            ];

            // Mark this picker session as having added a product
            addProductPickerHasChangesRef.current = true;

            // Show a brief success state on the card
            setAddProductPickerProductState(canonicalProductSku, "added");

            // Clear any previous reset timer for this same card
            if (addProductPickerStatusTimeoutsRef.current[canonicalProductSku]) {

                clearTimeout(
                    addProductPickerStatusTimeoutsRef.current[canonicalProductSku]
                );

            }

            // Return the card to its normal clickable state after the success fades
            addProductPickerStatusTimeoutsRef.current[canonicalProductSku] = setTimeout(() => {

                // Clear the card state and its stored timer
                setAddProductPickerProductState(canonicalProductSku, null);
                delete addProductPickerStatusTimeoutsRef.current[canonicalProductSku];

            }, 900);

        } catch (error) {

            // Restore the card after an unexpected request failure
            setAddProductPickerProductState(canonicalProductSku, null);

            // Show a useful generic request error
            toastError({
                title: "Error",
                message: "Could not add new product.",
                autoCloseMs: 6000
            });

        } finally {

            // Release one pending addition regardless of success or failure
            addProductPickerPendingCountRef.current -= 1;

            // Prevent the counter dropping below zero
            if (addProductPickerPendingCountRef.current < 0) {
                addProductPickerPendingCountRef.current = 0;
            }

            // Update the rendered pending count
            setAddProductPickerPendingCount(
                addProductPickerPendingCountRef.current
            );

        }

    };

    // Close the add product picker and save the design when products were added
    const closeAddProductPicker = async () => {

        // Do not close while product additions or another close request are running
        if (
            addProductPickerPendingCountRef.current > 0 ||
            addProductPickerIsClosingRef.current
        ) {
            return;
        }

        // Close immediately when the picker was opened without adding anything
        if (!addProductPickerHasChangesRef.current) {

            setAddProductPickerIsOpen(false);

            return;

        }

        // Lock the picker while its authoritative products are refreshed
        addProductPickerIsClosingRef.current = true;
        setAddProductPickerIsClosing(true);

        // Start without a products response
        let productsResponse = null;

        try {

            // Request the complete saved product hierarchy once
            productsResponse = await getPodDesignProducts(
                token,
                designOrTemplate,
                podDesignDetails.id
            );

        } catch (error) {

            // Show a useful generic request error
            toastError({
                title: "Error",
                message: "Could not refresh the design products.",
                autoCloseMs: 6000
            });

            // Unlock the picker for another attempt
            addProductPickerIsClosingRef.current = false;
            setAddProductPickerIsClosing(false);

            return;

        }

        // Keep the picker open if its final refresh fails
        if (!productsResponse?.success) {

            // Build a useful API error message
            let errorMessage = "Oops...something went wrong.";

            if (productsResponse?.error_message) {
                errorMessage = productsResponse.error_message;
            }

            // Show the refresh error
            toastError({
                title: "Error",
                message: "Could not refresh the design products: " + errorMessage,
                autoCloseMs: 6000
            });

            // Unlock the picker for another attempt
            addProductPickerIsClosingRef.current = false;
            setAddProductPickerIsClosing(false);

            return;

        }

        // Read the authoritative saved products safely
        let savedProducts = [];

        if (Array.isArray(productsResponse?.results)) {
            savedProducts = productsResponse.results;
        }

        // Check that every product added in this picker session is present in the refreshed hierarchy
        for (const localAddedProduct of addedProductsPendingSaveRef.current) {

            // Find the saved product by its real configured id
            const refreshedProduct = savedProducts.find((savedProduct) => {

                return String(savedProduct?.id || "") === String(localAddedProduct?.id || "");

            });

            // Keep the picker open rather than losing pending role or mockup data
            if (!refreshedProduct) {

                toastError({
                    title: "Error",
                    message: "A newly-added product was missing from the refreshed design products.",
                    autoCloseMs: 6000
                });

                addProductPickerIsClosingRef.current = false;
                setAddProductPickerIsClosing(false);

                return;

            }

        }

        // Restore local role and pending mockup data onto the authoritative saved hierarchy
        const productsForSave = mergeSavedProductsWithLocalPendingData(
            savedProducts,
            addedProductsPendingSaveRef.current
        );

        // Keep the live design state aligned with the authoritative products
        setCurrentProducts((prevDetails) => {

            // Return the updated design details
            return {
                ...prevDetails,
                products: productsForSave,
            };

        });

        // Close the picker before showing the normal complete design save screen
        setAddProductPickerIsOpen(false);

        // Clear any visual-state timers before resetting the picker
        Object.values(addProductPickerStatusTimeoutsRef.current).forEach((timeoutId) => {

            clearTimeout(timeoutId);

        });

        addProductPickerStatusTimeoutsRef.current = {};

        // Reset the picker session state
        addProductPickerHasChangesRef.current = false;
        addedProductsPendingSaveRef.current = [];
        addProductPickerProductStatesRef.current = {};
        setAddProductPickerProductStates({});
        addProductPickerIsClosingRef.current = false;
        setAddProductPickerIsClosing(false);

        // Do not attempt the save if the callback is unavailable
        if (typeof saveDesignAfterAddingProducts !== "function") { return; }

        // Run the normal complete design save with the refreshed products
        await saveDesignAfterAddingProducts(productsForSave);

    };

    // Store filters for the add product picker
    const [addProductFilters, setAddProductFilters] = useState({
        search: "",
        minPrice: "",
        maxPrice: "",
        printAreas: "",
        designRoles: [],
        roleVisibility: "available",
        colourwayMode: "",
        colourways: [],
        attributes: {},
    });

    // Auto select design role filters from the current design set
    useEffect(() => {

        // Get selected design set role keys
        const nextDesignRoles = designSetRoleOptions.map((roleOption) => roleOption.role);

        // Update filters without wiping user filters
        setAddProductFilters((prevFilters) => ({
            ...prevFilters,
            designRoles: nextDesignRoles,
        }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRoles, currentRoles]);

    // Toggle a value in an array
    const toggleArrayValue = (values, value) => {

        // Make sure we always have an array to work with
        const safeValues = Array.isArray(values) ? values : [];

        // Remove the value if it is already selected
        if (safeValues.includes(value)) {
            return safeValues.filter((existingValue) => existingValue !== value);
        }

        // Add the value if it is not already selected
        return [
            ...safeValues,
            value,
        ];

    };

    // Toggle an add product attribute filter
    const toggleAddProductAttributeFilter = (attributeName, attributeValue) => {

        // Update the selected attributes
        setAddProductFilters((prevFilters) => {

            // Get the current values for this attribute
            const currentValues = prevFilters.attributes?.[attributeName] || [];

            // Toggle the selected value
            const nextValues = toggleArrayValue(currentValues, attributeValue);

            // Build the next attributes object
            const nextAttributes = {
                ...(prevFilters.attributes || {}),
            };

            // Store or remove the attribute values
            if (nextValues.length > 0) {
                nextAttributes[attributeName] = nextValues;
            } else {
                delete nextAttributes[attributeName];
            }

            // Return the updated filters
            return {
                ...prevFilters,
                attributes: nextAttributes,
            };

        });

    };

    // Toggle an add product colourway filter
    const toggleAddProductColourwayFilter = (colourwayCode) => {

        // Update the selected colourways
        setAddProductFilters((prevFilters) => {

            // Toggle the selected colourway code
            const nextColourways = toggleArrayValue(prevFilters.colourways || [], colourwayCode);

            // Return the updated filters
            return {
                ...prevFilters,
                colourways: nextColourways,
            };

        });

    };

    // Clear the add product picker filters
    const clearAddProductFilters = () => {

        // Reset all filter values
        setAddProductFilters({
            search: "",
            minPrice: "",
            maxPrice: "",
            printAreas: "",
            designRoles: [],
            roleVisibility: "available",
            colourwayMode: "",
            colourways: [],
            attributes: {},
        });

    };

    // Build a lookup of canonical products by sku
    const canonicalProductsBySku = useMemo(() => {

        // Create the lookup
        const lookup = {};

        // Go through canonical products
        (canonicalProducts || []).forEach((product) => {

            // Add products with a sku to the lookup
            if (product?.sku) {
                lookup[product.sku] = product;
            }

        });

        // Return the lookup
        return lookup;

    }, [canonicalProducts]);

    // This creates an alphabetically sorted product list without mutating the original array
    const sortedCanonicalProducts = useMemo(() => {

        // This safely copies and sorts the canonical products
        return [...(canonicalProducts || [])].sort((productA, productB) => {

            // This gets the first product title safely
            const productATitle = productA?.Title || "";

            // This gets the second product title safely
            const productBTitle = productB?.Title || "";

            // This sorts titles alphabetically
            return productATitle.localeCompare(productBTitle);

        });

    }, [canonicalProducts]);

    // Get the parent product and child products as one searchable/filterable family
    const getCanonicalProductFamily = (canonicalProduct) => {

        // Start with the parent product
        const productFamily = [
            canonicalProduct,
        ];

        // Add child products if they exist
        if (Array.isArray(canonicalProduct?.children)) {
            canonicalProduct.children.forEach((childProduct) => productFamily.push(childProduct));
        }

        // Return the product family
        return productFamily;

    };



    // Get surfaces from a product row
    const getPodProductSurfaces = (product) => {

        // Get custom product surfaces
        const customProductSurfaces = product?.custom_product?.surfaces;

        // Get config product surfaces
        const configProductSurfaces = product?.custom_product_config?.surfaces;

        // Return the first valid surfaces array
        return Array.isArray(customProductSurfaces) ? customProductSurfaces : Array.isArray(configProductSurfaces) ? configProductSurfaces : [];

    };


    // Get this product's role rows in custom product surface and print area order
    const getPodProductOrderedRoles = (product) => {

        // Store role rows in the order supplied by the canonical custom product
        const orderedRoles = [];
        const addedRoleKeys = new Set();

        // Go through product surfaces in API order
        getPodProductSurfaces(product).forEach((surface) => {

            // Read the surface print areas safely
            let printAreas = [];

            if (Array.isArray(surface?.print_areas)) {
                printAreas = surface.print_areas;
            }

            // Go through surface print areas in API order
            printAreas.forEach((printArea) => {

                // Read the print area role key
                const roleKey = String(printArea?.role || "");

                // Skip print areas without a role or duplicate role rows
                if (!roleKey || addedRoleKeys.has(roleKey)) { return; }

                // Find the populated global role data without using its ordering
                const matchingRole = (allRoles || []).find((roleItem) => String(roleItem?.role || "") === roleKey);

                // Skip unknown role keys
                if (!matchingRole) { return; }

                // Add the role in this product's custom product order
                orderedRoles.push(matchingRole);
                addedRoleKeys.add(roleKey);

            });

        });

        // Return the ordered populated role rows
        return orderedRoles;

    };


    // Get all role keys supported by a parent or child product
    const getPodProductSupportedRoleKeys = (product) => {

        // Create role key set
        const roleKeys = new Set();

        // Go through product surfaces
        getPodProductSurfaces(product).forEach((surface) => {

            // Go through surface print areas
            (surface?.print_areas || []).forEach((printArea) => {

                // Add role key when available
                if (printArea?.role) {

                    roleKeys.add(String(printArea.role));

                }

            });

        });

        // Return role keys
        return roleKeys;

    };



    // Get all role keys supported by a parent product family
    const getCanonicalProductFamilySupportedRoleKeys = (canonicalProduct) => {

        // Create role key set
        const roleKeys = new Set();

        // Go through parent and child products
        getCanonicalProductFamily(canonicalProduct).forEach((product) => {

            // Add each supported role key
            getPodProductSupportedRoleKeys(product).forEach((roleKey) => {

                roleKeys.add(roleKey);

            });

        });

        // Return role keys
        return roleKeys;

    };


    // Find one canonical parent or child product by its SKU
    const getCanonicalProductBySku = (productSku) => {

        // Normalise the requested SKU
        const safeProductSku = String(productSku || "");

        // Stop when no SKU was supplied
        if (!safeProductSku) { return null; }

        // Read canonical parents safely
        let safeCanonicalProducts = [];

        if (Array.isArray(canonicalProducts)) {
            safeCanonicalProducts = canonicalProducts;
        }

        // Search every canonical family
        for (const canonicalProduct of safeCanonicalProducts) {

            // Return the canonical parent when it matches
            if (String(canonicalProduct?.sku || "") === safeProductSku) {
                return canonicalProduct;
            }

            // Read canonical children safely
            let canonicalChildren = [];

            if (Array.isArray(canonicalProduct?.children)) {
                canonicalChildren = canonicalProduct.children;
            }

            // Find a matching canonical child
            const matchingChild = canonicalChildren.find((canonicalChild) => {

                return String(canonicalChild?.sku || "") === safeProductSku;

            });

            // Return the matching child
            if (matchingChild) {
                return matchingChild;
            }

        }

        // No canonical parent or child matched
        return null;

    };


    // Add newly usable design roles supported by one configured parent or child
    const addNewUsableRolesToConfiguredProduct = (
        configuredProduct,
        canonicalProduct,
        newUsableRoleKeys
    ) => {

        // Stop when either product row is unavailable
        if (!configuredProduct || !canonicalProduct) {
            return {
                changed: false,
                product: configuredProduct,
            };
        }

        // Read the configured roles safely
        let configuredRoles = [];

        if (Array.isArray(configuredProduct?.roles)) {
            configuredRoles = configuredProduct.roles;
        }

        // Read the role keys this exact canonical parent or child supports
        const supportedRoleKeys =
            getPodProductSupportedRoleKeys(canonicalProduct);

        // Start with the existing configured roles
        const nextRoles = [
            ...configuredRoles,
        ];

        // Track whether a new role was added
        let rolesChanged = false;

        // Check every newly usable design role
        for (const roleKey of newUsableRoleKeys) {

            // Skip roles unsupported by this exact parent or child
            if (!supportedRoleKeys.has(roleKey)) { continue; }

            // Do not replace existing active, inactive or edited role settings
            const roleAlreadyExists = configuredRoles.some((configuredRole) => {

                return String(configuredRole?.role || "") === roleKey;

            });

            // Keep the existing role unchanged
            if (roleAlreadyExists) { continue; }

            // Build the default view settings from the new design image
            const viewSettingsResponse =
                buildDefaultViewSettingsForRole(roleKey);

            // Skip a role whose image dimensions cannot be resolved
            if (!viewSettingsResponse?.success) { continue; }

            // Add the missing configured product role
            nextRoles.push({
                role: roleKey,
                active: "Y",
                edited: "N",
                view_settings: viewSettingsResponse.results,
            });

            // Record the product change
            rolesChanged = true;

        }

        // Return the original product when nothing changed
        if (!rolesChanged) {
            return {
                changed: false,
                product: configuredProduct,
            };
        }

        // Return the configured product with its additional role rows
        return {
            changed: true,
            product: {
                ...configuredProduct,
                roles: nextRoles,
            },
        };

    };


    // Add newly imaged design roles to existing compatible configured products
    useEffect(() => {

        // Read the original role rows safely
        let safeOriginalRoles = [];

        if (Array.isArray(originalRoles)) {
            safeOriginalRoles = originalRoles;
        }

        // Store the role keys which already had usable images before this edit
        const originalUsableRoleKeys = new Set();

        // Check each original design role
        safeOriginalRoles.forEach((originalRole) => {

            // Read the original role key
            const originalRoleKey = String(originalRole?.role || "");

            // Skip malformed rows
            if (!originalRoleKey) { return; }

            // Check whether the original role had usable image data
            const originalRoleHadImage = Boolean(
                originalRole?.image_url ||
                originalRole?.thumbnail_url ||
                originalRole?.uploadedImage ||
                originalRole?.imageDetails?.image_url ||
                originalRole?.asset_id ||
                originalRole?.image_id
            );

            // Store usable original role keys
            if (originalRoleHadImage) {
                originalUsableRoleKeys.add(originalRoleKey);
            }

        });

        // Keep only roles which have become usable during the current edit
        const newUsableRoleKeys = Array.from(usableSelectedRoleKeys).filter(
            (roleKey) => {

                return !originalUsableRoleKeys.has(roleKey);

            }
        );

        // Nothing needs synchronising
        if (newUsableRoleKeys.length === 0) { return; }

        // Update the configured parent and child role rows
        setCurrentProducts((previousDetails) => {

            // Read the complete previous design details safely
            const safePreviousDetails = previousDetails || {};

            // Read configured product families safely
            let previousProducts = [];

            if (Array.isArray(safePreviousDetails?.products)) {
                previousProducts = safePreviousDetails.products;
            }

            // Nothing can be updated
            if (previousProducts.length === 0) {
                return previousDetails;
            }

            // Track whether any configured parent or child changed
            let productsChanged = false;

            // Update every configured product family
            const nextProducts = previousProducts.map((configuredProduct) => {

                // Start with the existing configured parent
                let nextConfiguredProduct = configuredProduct;

                // Read the configured parent SKU
                const configuredParentSku = String(
                    configuredProduct?.pod_product_sku ||
                    configuredProduct?.sku ||
                    ""
                );

                // Find the matching canonical parent
                const canonicalParent =
                    getCanonicalProductBySku(configuredParentSku);

                // Add any missing roles supported by the parent
                const parentRoleResult =
                    addNewUsableRolesToConfiguredProduct(
                        nextConfiguredProduct,
                        canonicalParent,
                        newUsableRoleKeys
                    );

                // Keep the updated parent
                nextConfiguredProduct = parentRoleResult.product;

                // Record the parent change
                if (parentRoleResult.changed) {
                    productsChanged = true;
                }

                // Read configured children safely
                let configuredChildren = [];

                if (Array.isArray(nextConfiguredProduct?.children)) {
                    configuredChildren = nextConfiguredProduct.children;
                }

                // Track whether any child changed
                let childrenChanged = false;

                // Update every configured child
                const nextChildren = configuredChildren.map((configuredChild) => {

                    // Read the configured child SKU
                    const configuredChildSku = String(
                        configuredChild?.pod_product_sku ||
                        configuredChild?.sku ||
                        ""
                    );

                    // Find the matching canonical child
                    const canonicalChild =
                        getCanonicalProductBySku(configuredChildSku);

                    // Add any missing roles supported by this child
                    const childRoleResult =
                        addNewUsableRolesToConfiguredProduct(
                            configuredChild,
                            canonicalChild,
                            newUsableRoleKeys
                        );

                    // Record the child change
                    if (childRoleResult.changed) {
                        childrenChanged = true;
                        productsChanged = true;
                    }

                    // Return the updated or unchanged child
                    return childRoleResult.product;

                });

                // Keep the current parent when none of its children changed
                if (!childrenChanged) {
                    return nextConfiguredProduct;
                }

                // Return the parent with its updated children
                return {
                    ...nextConfiguredProduct,
                    children: nextChildren,
                };

            });

            // Avoid a state update when no configured product supports the new role
            if (!productsChanged) {
                return previousDetails;
            }

            // Keep the immediate product reference aligned with the new state
            currentProductsRef.current = nextProducts;

            // Return the complete design with updated configured products
            return {
                ...safePreviousDetails,
                products: nextProducts,
            };

        });

    // The helper functions and usable role set are rebuilt from the state values listed below
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        canonicalProducts,
        currentRoles,
        designOrTemplate,
        originalRoles,
        selectedRoles,
        setCurrentProducts,
    ]);


    // Check whether any product family supports a selected role which currently has image data
    const noProductsMatchCriteria =
        usableSelectedRoleKeys.size === 0 ||
        !(canonicalProducts || []).some((canonicalProduct) => {

            // Get every role supported by this parent and its children
            const supportedRoleKeys =
                getCanonicalProductFamilySupportedRoleKeys(canonicalProduct);

            // Check whether the family supports any currently usable design role
            return Array.from(usableSelectedRoleKeys).some((roleKey) => {

                return supportedRoleKeys.has(roleKey);

            });

        });

    // Get attributes from a product row
    const getPodProductAttributes = (product) => {

        // Create the attribute map
        const attributes = {};

        // Go through all attribute rows
        (product?.attributes || []).forEach((attributeRow) => {

            // Go through each key/value pair in the row
            Object.entries(attributeRow || {}).forEach(([attributeName, attributeValue]) => {

                // Skip empty attributes
                if (!attributeName || attributeValue === null || attributeValue === undefined || attributeValue === "") { return; }

                // Make sure the attribute bucket exists
                if (!attributes[attributeName]) { attributes[attributeName] = []; }

                // Add the value if it is not already there
                if (!attributes[attributeName].includes(String(attributeValue))) {
                    attributes[attributeName].push(String(attributeValue));
                }

            });

        });

        // Return the attributes
        return attributes;

    };

    // Get sellable colourway codes from config only
    const getPodProductConfiguredColourwayCodes = (product) => {

        // Get colourways from the top-level colourway config if present
        const topLevelColourways = product?.colourways_config?.available;

        // Get colourways from the custom product config if present
        const configColourways = product?.custom_product_config?.colourways?.available;

        // Use the config source that exists
        const availableColourways = Array.isArray(topLevelColourways) ? topLevelColourways : configColourways;

        // Return a clean list of configured colourway codes
        return (Array.isArray(availableColourways) ? availableColourways : [])
            .map((colourwayCode) => String(colourwayCode || ""))
            .filter(Boolean);

    };

    // Get metadata for a configured colourway code
    const getPodProductColourwayMeta = (product, colourwayCode) => {

        // Get the custom product colourway metadata
        const colourways = product?.custom_product?.colourways || [];

        // Find the matching metadata row
        const colourway = colourways.find((row) => String(row?.code || "") === String(colourwayCode || ""));

        // Return a safe metadata object
        return {
            code: String(colourwayCode || ""),
            name: colourway?.name || String(colourwayCode || ""),
            hex_colour: colourway?.hex_colour || "",
        };

    };

    // Count print areas across all product surfaces
    const getPodProductPrintAreaCount = (product) => {

        // Get the custom product surfaces first
        const customProductSurfaces = product?.custom_product?.surfaces;

        // Fall back to the config surfaces if needed
        const configSurfaces = product?.custom_product_config?.surfaces;

        // Use the available surfaces
        const surfaces = Array.isArray(customProductSurfaces) ? customProductSurfaces : configSurfaces;

        // Count all print areas
        return (Array.isArray(surfaces) ? surfaces : []).reduce((total, surface) => {

            // Add the print area count for this surface
            return total + ((surface?.print_areas || []).length);

        }, 0);

    };

    // Get usable prices for a product family
    const getPodProductFamilyPrices = (canonicalProduct) => {

        // Get all numeric prices from parent and children
        const prices = getCanonicalProductFamily(canonicalProduct)
            .map((product) => Number(product?.price))
            .filter((price) => Number.isFinite(price));

        // Prefer positive prices where available because parent rows may carry placeholder zero prices
        const positivePrices = prices.filter((price) => price > 0);

        // Return the usable prices
        return positivePrices.length > 0 ? positivePrices : prices;

    };

    // Work out the available price range for the add product picker
    const addProductPriceBounds = useMemo(() => {

        // Collect prices from all canonical product families
        const prices = (canonicalProducts || [])
            .flatMap((canonicalProduct) => getPodProductFamilyPrices(canonicalProduct))
            .filter((price) => Number.isFinite(price));

        // If there are no prices, return a safe empty range
        if (prices.length === 0) {
            return {
                min: 0,
                max: 0,
            };
        }

        // Return min and max prices rounded to two decimals
        return {
            min: Math.floor(Math.min(...prices) * 100) / 100,
            max: Math.ceil(Math.max(...prices) * 100) / 100,
        };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canonicalProducts]);



    // Check whether a product matches the current selected print-area role criteria
    const addProductMatchesSelectedRoles = (canonicalProduct) => {

        // Get supported role keys from parent and children
        const supportedRoleKeys = getCanonicalProductFamilySupportedRoleKeys(canonicalProduct);

        // Get active design role filters
        const activeDesignRoleFilters = Array.isArray(addProductFilters.designRoles) && addProductFilters.designRoles.length > 0
            ? addProductFilters.designRoles
            : designSetRoleOptions.map((roleOption) => roleOption.role);

        // Return true if this product family supports any selected design role filter
        return activeDesignRoleFilters.some((roleKey) => supportedRoleKeys.has(String(roleKey)));

    };



    // Build add product picker filter options
    const addProductFilterOptions = useMemo(() => {

        // Create filter option stores
        const attributeOptions = {};
        const colourwayOptions = {};

        // Go through all canonical products
        (canonicalProducts || []).forEach((canonicalProduct) => {

            // Go through parent and child products
            getCanonicalProductFamily(canonicalProduct).forEach((product) => {

                // Get product attributes
                const attributes = getPodProductAttributes(product);

                // Add attribute values
                Object.entries(attributes).forEach(([attributeName, attributeValues]) => {

                    // Make sure the attribute option bucket exists
                    if (!attributeOptions[attributeName]) { attributeOptions[attributeName] = new Set(); }

                    // Add each attribute value
                    attributeValues.forEach((attributeValue) => attributeOptions[attributeName].add(attributeValue));

                });

                // Get configured colourway codes only
                getPodProductConfiguredColourwayCodes(product).forEach((colourwayCode) => {

                    // Get display metadata for the configured colourway
                    const colourwayMeta = getPodProductColourwayMeta(product, colourwayCode);

                    // Build the colourway grouping key from the display name
                    const colourwayKey = String(colourwayMeta.name || colourwayCode || "").trim().toLowerCase();

                    // If this colourway name is not already stored, add it with the first hex example
                    if (!colourwayOptions[colourwayKey]) {
                        colourwayOptions[colourwayKey] = {
                            key: colourwayKey,
                            name: colourwayMeta.name || String(colourwayCode || ""),
                            hex_colour: colourwayMeta.hex_colour || "",
                            codes: [],
                        };
                    }

                    // Add the configured colourway code to this grouped colourway option
                    if (!colourwayOptions[colourwayKey].codes.includes(colourwayCode)) {
                        colourwayOptions[colourwayKey].codes.push(colourwayCode);
                    }

                });

            });

        });

        // Convert attribute sets to sorted arrays
        const sortedAttributeOptions = {};
        Object.entries(attributeOptions).forEach(([attributeName, attributeValues]) => {

            // Sort the attribute values
            sortedAttributeOptions[attributeName] = Array.from(attributeValues).sort((valueA, valueB) => valueA.localeCompare(valueB));

        });

        // Return sorted filter options
        return {
            attributes: sortedAttributeOptions,
            colourways: Object.values(colourwayOptions).sort((colourwayA, colourwayB) => colourwayA.name.localeCompare(colourwayB.name)),
        };

    }, [canonicalProducts]);

    // Check whether a product family matches the active add product filters
    const addProductMatchesFilters = (canonicalProduct) => {

        // Get parent and child products as one family
        const productFamily = getCanonicalProductFamily(canonicalProduct);

        // Get the search term
        const searchTerm = String(addProductFilters.search || "").trim().toLowerCase();

        // Apply search filter
        if (searchTerm) {

            // Check if any family row matches the search term
            const searchMatches = productFamily.some((product) => {

                // Build searchable text
                const searchableText = [
                    product?.sku,
                    product?.Title,
                    product?.customcode,
                    product?.custom_product_config?.product_code,
                    product?.custom_product?.product_code,
                ].filter(Boolean).join(" ").toLowerCase();

                // Return whether this product matches
                return searchableText.includes(searchTerm);

            });

            // Stop if search does not match
            if (!searchMatches) { return false; }

        }

        // Apply dynamic attribute filters
        for (const [attributeName, selectedValues] of Object.entries(addProductFilters.attributes || {})) {

            // Skip empty selected values
            if (!Array.isArray(selectedValues) || selectedValues.length === 0) { continue; }

            // Check whether any family row has any selected value for this attribute
            const attributeMatches = productFamily.some((product) => {

                // Get product attributes
                const attributes = getPodProductAttributes(product);

                // Get the values for this attribute
                const productValues = attributes[attributeName] || [];

                // Check whether any selected value is present
                return selectedValues.some((selectedValue) => productValues.includes(selectedValue));

            });

            // Stop if this attribute does not match
            if (!attributeMatches) { return false; }

        }

        // Get usable prices for the family
        const familyPrices = getPodProductFamilyPrices(canonicalProduct);

        // Get selected price bounds
        const minPrice = addProductFilters.minPrice !== "" ? Number(addProductFilters.minPrice) : null;
        const maxPrice = addProductFilters.maxPrice !== "" ? Number(addProductFilters.maxPrice) : null;

        // Apply price range filter
        if (minPrice !== null || maxPrice !== null) {

            // Check if any product in the family sits inside the selected price range
            const priceMatches = familyPrices.some((price) => {

                // Check the minimum price if supplied
                if (Number.isFinite(minPrice) && price < minPrice) { return false; }

                // Check the maximum price if supplied
                if (Number.isFinite(maxPrice) && price > maxPrice) { return false; }

                // This price is inside the selected range
                return true;

            });

            // Stop if no family price is inside the selected range
            if (!priceMatches) { return false; }

        }

        // Get print area counts for the product family
        const printAreaCounts = productFamily.map((product) => getPodProductPrintAreaCount(product)).filter((count) => count > 0);

        // Apply print area filter
        if (addProductFilters.printAreas) {

            // Check whether any product in the family matches the selected print area bucket
            const printAreasMatch = printAreaCounts.some((printAreaCount) => {

                // Match one print area
                if (addProductFilters.printAreas === "1") { return printAreaCount === 1; }

                // Match two print areas
                if (addProductFilters.printAreas === "2") { return printAreaCount === 2; }

                // Match three or more print areas
                if (addProductFilters.printAreas === "3+") { return printAreaCount >= 3; }

                // Default to no match
                return false;

            });

            // Stop if print areas do not match
            if (!printAreasMatch) { return false; }

        }

        // Get configured colourway filter keys for the family
        const familyColourwayKeys = new Set();

        productFamily.forEach((product) => {

            // Go through configured colourway codes only
            getPodProductConfiguredColourwayCodes(product).forEach((colourwayCode) => {

                // Get display metadata for this configured colourway
                const colourwayMeta = getPodProductColourwayMeta(product, colourwayCode);

                // Build the same grouped colourway key used by the filter options
                const colourwayKey = String(colourwayMeta.name || colourwayCode || "").trim().toLowerCase();

                // Add the grouped key
                if (colourwayKey) {
                    familyColourwayKeys.add(colourwayKey);
                }

            });

        });

        // Apply colourway mode filter
        if (addProductFilters.colourwayMode === "has" && familyColourwayKeys.size === 0) { return false; }
        if (addProductFilters.colourwayMode === "none" && familyColourwayKeys.size > 0) { return false; }

        // Apply specific colourway filters
        if (Array.isArray(addProductFilters.colourways) && addProductFilters.colourways.length > 0) {

            // Check whether any selected grouped colourway is configured for this family
            const colourwayMatches = addProductFilters.colourways.some((colourwayKey) => familyColourwayKeys.has(colourwayKey));

            // Stop if no selected colourways match
            if (!colourwayMatches) { return false; }

        }

        // Product matches all filters
        return true;

    };

    // Build the filtered add product picker product list
    const filteredAddProductPickerProducts = useMemo(() => {

        // Filter the sorted canonical products
        return sortedCanonicalProducts.filter((canonicalProduct) => {

            // Respect the printable area matching rules first
            if (!addProductMatchesSelectedRoles(canonicalProduct)) { return false; }

            // Apply add product picker filters
            return addProductMatchesFilters(canonicalProduct);

        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortedCanonicalProducts, addProductFilters, selectedRoles]);

    // Build the live design preview data for one add-product card
    const getAddProductPickerPreviewData = (canonicalProduct, matchingUsableRoleKeys) => {

        // Stop when the product family has no usable matching design role
        if (
            !Array.isArray(matchingUsableRoleKeys) ||
            matchingUsableRoleKeys.length < 1
        ) {
            return null;
        }

        // Use the first matching design role in the user's selected role order
        const previewRoleKey = String(matchingUsableRoleKeys[0] || "");

        // Stop when the preview role cannot be identified
        if (!previewRoleKey) { return null; }

        // Start without a canonical product row for the preview
        let previewCanonicalProduct = null;

        // Find the first parent or child product which supports this exact role
        for (const familyProduct of getCanonicalProductFamily(canonicalProduct)) {

            // Check the role keys supported by this family member
            const supportedRoleKeys = getPodProductSupportedRoleKeys(familyProduct);

            // Keep the first family member which can display this role
            if (supportedRoleKeys.has(previewRoleKey)) {

                previewCanonicalProduct = familyProduct;

                break;

            }

        }

        // Stop when no family member can display the selected role
        if (!previewCanonicalProduct) { return null; }

        // Build the same default view restrictions that will be saved for this role
        const viewSettingsResults = buildDefaultViewSettingsForRole(previewRoleKey);

        // Stop when the design image dimensions are unavailable
        if (!viewSettingsResults.success) { return null; }

        // Build a temporary configured role for selecting the correct live product view
        const previewConfiguredRole = {
            role: previewRoleKey,
            active: "Y",
            edited: "N",
            view_settings: viewSettingsResults.results,
        };

        // Get the first real product view allowed by the role's aspect and rotation settings
        const previewView = getFirstConfiguredProductPreviewView(
            previewCanonicalProduct,
            previewConfiguredRole,
            previewRoleKey
        );

        // Stop when no compatible live product view exists
        if (!previewView) { return null; }

        // Find the current design image data for this role
        const previewDesignRole = currentRoles.find((roleItem) => {

            return String(roleItem?.role || "") === previewRoleKey;

        });

        // Stop when the role no longer has design data
        if (!previewDesignRole) { return null; }

        // Start without a selected colourway
        let selectedColourwayCode = "";

        // Use the first canonical colourway when this product supports colourways
        const availableColourwayCodes = getAvailableProductColourwayCodes(
            previewCanonicalProduct
        );

        if (availableColourwayCodes.length > 0) {
            selectedColourwayCode = availableColourwayCodes[0];
        }

        // Return everything required by the preview component
        return {
            canonicalProduct: previewCanonicalProduct,
            configuredRole: previewConfiguredRole,
            designRole: previewDesignRole,
            view: previewView,
            selectedColourwayCode: selectedColourwayCode,
        };

    };



    // Delete one configured product from the design or template
    const handleDeleteConfiguredProduct = ({ configuredProduct, configuredProductIndex, configuredProductKey, canonicalProduct }) => {

        // Display confirmation before deleting the configured product
        DisplayConfirmationDialog(
            `<h3>Are you sure?</h3><br/>This will remove <strong>${canonicalProduct.Title}</strong>${configuredProduct?.id ? " #" + configuredProduct.id : ""} from this ${designOrTemplate}.<br/><br/><span class='warning-message'>WARNING: This cannot be undone.</span>`,
            "Delete",
            "Cancel",
            async function (is_confirmed) {

                // If user cancelled, stop
                if (!is_confirmed) { return; }

                // Set loading state
                setLoadingProductActiveToggle(configuredProductKey);

                // If this product has already been saved, delete it via the API first
                if (configuredProduct?.id) {

                    // Delete the configured product
                    const response = await deleteDesignProduct(token, designOrTemplate, podDesignDetails.id, configuredProduct.id);

                    // If the delete failed, show the error and stop
                    if (!response.success) {

                        // Clear loading state
                        setLoadingProductActiveToggle(null);

                        // Toast error message
                        toastError({ title: "Error", message: "Could not delete product: " + response.error_message, autoCloseMs: 6000 });

                        // Stop here
                        return;

                    }

                }

                // Remove the configured product from current state
                setCurrentProducts((prevDetails) => {

                    // This ensures products is always an array
                    const prevProducts = Array.isArray(prevDetails?.products) ? prevDetails.products : [];

                    // Remove the matching configured product
                    const nextProducts = prevProducts.filter((productItem, productIndex) => {

                        // Match saved products by configured id
                        if (configuredProduct?.id) {
                            return String(productItem?.id || "") !== String(configuredProduct.id || "");
                        }

                        // Match unsaved products by array index
                        return productIndex !== configuredProductIndex;

                    });

                    // Return updated state
                    return {
                        ...prevDetails,
                        products: nextProducts,
                    };

                });

                // Clear loading state
                setLoadingProductActiveToggle(null);

            }
        );

    };

    // Build configured product display rows from current products
    const configuredProductRows = useMemo(() => {

        // Ensure products is always an array
        const safeCurrentProducts = Array.isArray(currentProducts) ? currentProducts : [];

        // Build configured rows
        return safeCurrentProducts
            .map((configuredProduct, configuredProductIndex) => {

                // Get the matching canonical product
                const canonicalProduct = canonicalProductsBySku[configuredProduct?.pod_product_sku] || null;

                // Skip products where the canonical product cannot be found
                if (!canonicalProduct) { return null; }

                // Return display row
                return {
                    configuredProduct: configuredProduct,
                    configuredProductIndex: configuredProductIndex,
                    canonicalProduct: canonicalProduct,
                };

            })
            .filter(Boolean)
            .sort((rowA, rowB) => {

                // Check whether each product is unsaved
                const rowAIsUnsaved = !rowA?.configuredProduct?.id;
                const rowBIsUnsaved = !rowB?.configuredProduct?.id;

                // Unsaved products should appear before saved products
                if (rowAIsUnsaved !== rowBIsUnsaved) {
                    return rowAIsUnsaved ? -1 : 1;
                }

                // Sort unsaved products by newest client-created timestamp first
                if (rowAIsUnsaved && rowBIsUnsaved) {
                    return Number(rowB?.configuredProduct?.client_created_at || 0) - Number(rowA?.configuredProduct?.client_created_at || 0);
                }

                // Sort saved configured products by newest configured id first
                return Number(rowB?.configuredProduct?.id || 0) - Number(rowA?.configuredProduct?.id || 0);

            });

    }, [currentProducts, canonicalProductsBySku]);



    // Check if any of the roles has image data - we want to disable the add products button if it's false
    const anyRoleHasImageData = podDesignDetails?.roles?.some((role) => Boolean(role.image_url)) ?? false;

    // Set the Add Product tooltip for the current design or template state
    let addProductButtonTitle = "Add Product";

    // Explain why products cannot be added when there is no usable image
    if (!anyRoleHasImageData) {
        addProductButtonTitle = "Cannot add products until a print area has image data";
    }

    // Unsaved templates need an id before products can be added
    if (isAddingNewDesign && designOrTemplate === "template") {
        addProductButtonTitle = "Save this template before adding products";
    }

    // Track whether this is a new template which cannot yet have products
    const unsavedTemplateCannotAddProducts = isAddingNewDesign && designOrTemplate === "template";

    // Track whether this is a new design which cannot yet have products
    const unsavedDesignCannotAddProducts = isAddingNewDesign && designOrTemplate === "design";



    // Get the selected minimum price
    const selectedMinPrice =
        addProductFilters.minPrice !== ""
            ? Number(addProductFilters.minPrice)
            : Number(addProductPriceBounds.min);



    // Get the selected maximum price
    const selectedMaxPrice =
        addProductFilters.maxPrice !== ""
            ? Number(addProductFilters.maxPrice)
            : Number(addProductPriceBounds.max);



    return (

        <div>

            <div className="horizontal-align-content-center flex-justify-left-right">

                <div></div>

                {/* Top actions bar above the products grid */}
                <RenderProductGridActionsBar
                    handleClearAllParentAndChildProductsRoleData={handleClearAllParentAndChildProductsRoleData}
                    someProductRolesHaveBeenEdited={someProductRolesHaveBeenEdited}
                />



            </div>


            <br />

            {/* If there's no canonical products */}
            {canonicalProducts.length === 0 &&

                <p>There are no POD products available.</p>

            }

            {/* Explain when no products support the selected printable areas */}
            {noProductsMatchCriteria &&
                canonicalProducts.length > 0 &&
                !unsavedTemplateCannotAddProducts &&
                !unsavedDesignCannotAddProducts &&

                <div className="pod-design__products-save-template-message">
                    No products match your current selected print areas.
                </div>

            }

            {/* Explain what must be completed before products can be added to a new template */}
            {unsavedTemplateCannotAddProducts &&

                <div className="pod-design__products-save-template-message">
                    Give the template a Title and Code, then save before adding products.
                </div>

            }

            {/* Explain what must be completed before products can be added to a new design */}
            {unsavedDesignCannotAddProducts &&

                <div className="pod-design__products-save-template-message">
                    Add an image before adding products.
                </div>

            }

            {/* Product grid */}

            {canonicalProducts?.length > 0 &&

                <>

                    <div id="products" className="strich_masonry" style={{ "--grid-column-width": "300px" }}>


                        {/* Display the Add Product button only when at least one product matches the selected print areas */}
                        {!isStrichTemplate &&
                            !unsavedTemplateCannotAddProducts &&
                            !unsavedDesignCannotAddProducts &&
                            !noProductsMatchCriteria &&

                            <button
                                className="strich_masonry_designset pod-design__product pod-design__add-product-button" 
                                onClick={handleOpenAddProductPicker}
                                title={addProductButtonTitle}
                                disabled={!anyRoleHasImageData || isAddingNewDesign}
                                
                            >

                                <div>

                                    <div className="pod-design__add-product-button-title">
                                        +
                                    </div>

                                    <h2 style={{ margin: "0.5rem 0 0 0" }}>Add Product</h2>
                                    

                                    {isAddingNewDesign && designOrTemplate === "template" &&

                                        <span className="warning-message">
                                            Save this template before adding products.
                                        </span>

                                    }

                                    {isAddingNewDesign && designOrTemplate !== "template" &&

                                        <span className="warning-message">
                                            Add an image before adding products.
                                        </span>

                                    }

                                    {!isAddingNewDesign && anyRoleHasImageData &&

                                        <p>Choose which products to add to this {designOrTemplate}</p>

                                    }

                                    {!isAddingNewDesign && !anyRoleHasImageData &&

                                        <span className="warning-message">
                                            You need to add an image to a print area before you can add products.
                                        </span>

                                    }

                                </div>

                            </button>

                        }


                        {/* Display all the products associated with the design set */}
                        {configuredProductRows.map(({ configuredProduct, configuredProductIndex, canonicalProduct }) => {

                            // Get the surfaces
                            const surfaces = canonicalProduct?.custom_product?.surfaces || [];

                            // If there isn't any surfaces return early
                            if (surfaces?.length === 0) { return null; }

                            // Count unique matching printArea roles across all surfaces
                            const displayRoleCount = new Set(

                                // Flatten all print areas across all surfaces
                                (surfaces || [])
                                    .flatMap((surface) => (surface?.print_areas || []))

                                    // Keep only print areas whose role is selected (with role image)
                                    //.filter((printArea) => selectedRoleKeys.has(String(printArea?.role || "")))
                                    // Keep only print areas whose role is selected (although it might not have an image)
                                    .filter((printArea) => selectedRoles?.find((selectedRole) => selectedRole.role === printArea.role).selected)

                                    // Map to role key
                                    .map((printArea) => String(printArea?.role || ""))

                            ).size;

                            if (displayRoleCount < 1) {
                                return null;
                            }

                            // Get row key
                            const configuredProductKey = configuredProduct?.id || configuredProduct?.client_configured_product_id || "new-" + configuredProductIndex;

                            // Check active state for this configured product only
                            const isAssignedProduct = configuredProduct?.active === "Y";

                            // Check whether the configured parent has edited placement data
                            let productHasEditedRole = Array.isArray(configuredProduct?.roles) && configuredProduct.roles.some((roleItem) => String(roleItem?.edited || "N") === "Y");

                            // A child edit also makes the complete product family resettable from this card
                            if (!productHasEditedRole && Array.isArray(configuredProduct?.children)) {

                                productHasEditedRole = configuredProduct.children.some((childItem) => {

                                    // Read child roles safely
                                    const childRoles = Array.isArray(childItem?.roles) ? childItem.roles : [];

                                    // Return whether this child contains an edited placement
                                    return childRoles.some((roleItem) => String(roleItem?.edited || "N") === "Y");

                                });

                            }

                            // Store the worst print quality state on this product or any active child
                            let productPrintQualityStatus = "";

                            // Check the configured parent and every configured child
                            const productPrintQualityMembers = [
                                configuredProduct,
                                ...(Array.isArray(configuredProduct?.children) ? configuredProduct.children : [])
                            ];

                            for (const productPrintQualityMember of productPrintQualityMembers) {

                                // An inactive child does not contribute to the current product family
                                if (productPrintQualityMember !== configuredProduct && productPrintQualityMember?.active === "N") { continue; }

                                // Read this parent or child's configured print areas
                                const productPrintQualityRoles = Array.isArray(productPrintQualityMember?.roles)
                                    ? productPrintQualityMember.roles
                                    : [];

                                for (const productPrintQualityRole of productPrintQualityRoles) {

                                    // Ignore disabled print areas
                                    if (productPrintQualityRole?.active === "N") { continue; }

                                    // Read the API-calculated status
                                    const rolePrintQualityStatus = String(productPrintQualityRole?.print_quality?.status || "").toLowerCase();

                                    // A failed print area is the worst possible state
                                    if (rolePrintQualityStatus === "fail") {
                                        productPrintQualityStatus = "fail";
                                        break;
                                    }

                                    // Remember a warning unless a failure is found later
                                    if (rolePrintQualityStatus === "warning" && productPrintQualityStatus === "") {
                                        productPrintQualityStatus = "warning";
                                    }

                                }

                                // Nothing can be worse than a failure
                                if (productPrintQualityStatus === "fail") { break; }

                            }

                            // Build the summary explanation for the product card
                            let productPrintQualityMessage = "";

                            if (productPrintQualityStatus === "fail") {
                                productPrintQualityMessage = "One or more print areas on this product have image resolution below the minimum requirement. Open the product to review the affected print area.";
                            }

                            if (productPrintQualityStatus === "warning") {
                                productPrintQualityMessage = "One or more print areas on this product have image resolution below the recommended level. Open the product to review the affected print area.";
                            }

                            return (

                                <div
                                    key={configuredProductKey}
                                    className="strich_masonry_designset pod-design__product"
                                    style={{
                                        gridColumn: `span ${displayRoleCount ?? 1}`,
                                        padding: "1rem 0.5rem",
                                        cursor: "pointer",
                                        position: "relative",
                                        opacity: isAssignedProduct || (podProductDetails?.sku === canonicalProduct.sku && productDetailsPopupIsOpen && String(selectedConfiguredProductId || "") === String(configuredProductKey || "")) ? "1" : "0.5",
                                    }}
                                    onClick={() => {

                                        // Click function to get product details for this configured product instance
                                        handleGetPodProductDetails(canonicalProduct.sku, configuredProductKey);

                                    }}
                                    title="View Product"
                                    ref={setItemRef(configuredProductKey)}
                                >

                                    <div className="horizontal-align-content-center flex-justify-left-right pod-design__product-title">

                                        <div>

                                            <p>
                                                <b>{canonicalProduct.Title}</b>
                                            </p>

                                        </div>

                                        <div className="pod-design__product-toggle"

                                            // Stop click bubbling to the parent
                                            onClick={(event) => {

                                                // Stop the click bubbling up
                                                event.stopPropagation();

                                            }}
                                        >

                                            {loadingProductActiveToggle === (configuredProductKey || "all") ?

                                                <LoadingDotsAnimation />

                                                :

                                                <div className="horizontal-align-content-center">

                                                    {/* Display warnings and lock symbol if this is a basic set */}
                                                    {!isAddingAdvancedDesignSet &&

                                                        <div className="horizontal-align-content-center" style={{ gap: "0.35rem" }}>

                                                            {productHasEditedRole &&

                                                                <button
                                                                    type="button"
                                                                    className="pod-pricing-page__plan-badge pod-design__edited-badge"
                                                                    title="This product has edited design placement. Click to reset the product and its children."
                                                                    aria-label={`Reset edited placement for ${canonicalProduct.Title}`}
                                                                    onClick={(event) => {

                                                                        // Do not open the product popup when the edited pill is clicked
                                                                        event.stopPropagation();
                                                                        event.preventDefault();

                                                                        // Reset this configured parent and all of its children
                                                                        handleClearParentAndChildProductRoleData({
                                                                            configuredProduct: configuredProduct,
                                                                            configuredProductIndex: configuredProductIndex,
                                                                            canonicalProduct: canonicalProduct,
                                                                        });

                                                                    }}
                                                                >
                                                                    Edited
                                                                </button>

                                                            }

                                                        </div>

                                                    }

                                                    <ToggleSwitch
                                                        size="s"
                                                        name={String(configuredProductKey)}
                                                        title={Boolean(isAssignedProduct) ? "Disable Product" : "Enable Product"}
                                                        value={Boolean(isAssignedProduct)}
                                                        onChange={(event) => {

                                                            // Work out next checked value
                                                            const nextChecked = event.target.value === undefined ? !Boolean(isAssignedProduct) : event.target.value;

                                                            // Set loading state
                                                            setLoadingProductActiveToggle(configuredProductKey);

                                                            // Update this configured product only
                                                            setCurrentProducts((prevDetails) => {

                                                                // This ensures products is always an array
                                                                const prevProducts = Array.isArray(prevDetails?.products) ? prevDetails.products : [];

                                                                // Build next product array
                                                                const nextProducts = prevProducts.map((productItem, productIndex) => {

                                                                    // Match saved products by configured id
                                                                    if (configuredProduct?.id && String(productItem?.id || "") !== String(configuredProduct.id || "")) {
                                                                        return productItem;
                                                                    }

                                                                    // Match unsaved products by array index
                                                                    if (!configuredProduct?.id && productIndex !== configuredProductIndex) {
                                                                        return productItem;
                                                                    }

                                                                    // Return updated configured product
                                                                    return {
                                                                        ...productItem,
                                                                        active: nextChecked ? "Y" : "N",
                                                                    };

                                                                });

                                                                // Return updated state
                                                                return {
                                                                    ...prevDetails,
                                                                    products: nextProducts,
                                                                };

                                                            });

                                                            // The product toggle belongs to the main design-page Save button
                                                            markProductGridChangesPending?.();

                                                            // Clear loading state
                                                            setLoadingProductActiveToggle(null);

                                                        }}
                                                    />

                                                </div>

                                            }

                                        </div>

                                    </div>

                                    <div className="pod-design__product-images">

                                        {currentRoles.length > 0 ?

                                            getPodProductOrderedRoles(canonicalProduct).map((role) => {

                                                // Check that the role is selected - if so show it else don't show it
                                                const roleIsSelected = selectedRoles.find((r) => r.role === role.role).selected;

                                                // Safely iterate surfaces
                                                const match = surfaces.some((surface) =>
                                                    (surface?.print_areas || []).find((printArea) =>
                                                        printArea?.role === role.role
                                                    )
                                                );

                                                const currentProductRole = configuredProduct?.roles?.find((r) => r?.role === role?.role) || null;
                                                const currentDesignRole = currentRoles.find((r) => r.role === role?.role) || null;

                                                // If this isn't a matching role in the canonical product then return null
                                                if (!match) { return null; }

                                                // Get the first live view allowed by this configured design product role
                                                const firstConfiguredView = getFirstConfiguredProductPreviewView(
                                                    canonicalProduct,
                                                    currentProductRole,
                                                    role?.role
                                                );

                                                // Do not substitute an unrelated canonical or custom-product view
                                                if (!firstConfiguredView) { return null; }

                                                // If the role is selected but has no design data, or the current product role is inactive, show an empty product surface
                                                if ((!currentDesignRole && roleIsSelected) || currentProductRole?.active === "N") {

                                                    return (

                                                        <div key={role.role} className="masonry_product">

                                                            <PodProductPreview
                                                                thedesign={{ role: role.role }}
                                                                product={canonicalProduct.custom_product}
                                                                currentProductRoleData={configuredProduct?.roles?.find((r) => r?.role === role.role) || null}
                                                                mockup_height={180}
                                                                options={{
                                                                    display_mode: "product",
                                                                    surface_code: firstConfiguredView.surface_code || "default",
                                                                    print_area_code: firstConfiguredView.print_area_code || "default",
                                                                    view_style: firstConfiguredView.style || "default",
                                                                    view_name: firstConfiguredView.name || "",
                                                                    selected_colourway_code: configuredProduct?.default_colourway || "",
                                                                }}
                                                            />

                                                            <div className="pod-design__product-role-details horizontal-align-content-center flex-justify-left-right" onClick={(event) => event.stopPropagation()}>

                                                                {isAddingAdvancedDesignSet ?

                                                                    <div className="pod-design__design-set-image-title horizontal-align-content-center">

                                                                        {printAreaRoles.find((roleItem) => roleItem.role === role.role).name}

                                                                        <ToggleSwitch
                                                                            size="xs"
                                                                            name={String(configuredProductKey)}
                                                                            value={currentProductRole ? S2B(currentProductRole.active) : true}
                                                                            onChange={(event) => toggleDesignProductRoleActiveSate(event.target.value, role, configuredProduct)}
                                                                        />

                                                                    </div>

                                                                    :

                                                                    <div></div>

                                                                }

                                                            </div>

                                                        </div>

                                                    );

                                                }

                                                // If the role isn't selected
                                                if (!roleIsSelected) { return null; }

                                                // Return the build mockup component
                                                return (

                                                    <div key={role.role} className="masonry_product" style={{ position: "relative" }}>

                                                        <PodProductPreview
                                                            thedesign={currentDesignRole.suppress_product_preview ? { role: role.role } : currentDesignRole}
                                                            product={canonicalProduct.custom_product}
                                                            currentProductRoleData={currentProductRole}
                                                            mockup_height={isAddingAdvancedDesignSet ? 180 : 195}
                                                            options={{
                                                                display_mode: "product",
                                                                product_fit_fill: currentProductRole?.fit_fill ? currentProductRole?.fit_fill : null,
                                                                surface_code: firstConfiguredView.surface_code || "default",
                                                                print_area_code: firstConfiguredView.print_area_code || "default",
                                                                view_style: firstConfiguredView.style || "default",
                                                                view_name: firstConfiguredView.name || "",
                                                                selected_colourway_code: configuredProduct?.default_colourway || "",
                                                            }}
                                                        />

                                                        <div className="pod-design__product-role-details horizontal-align-content-center flex-justify-left-right" onClick={(event) => event.stopPropagation()}>

                                                            {isAddingAdvancedDesignSet ?

                                                                <div className="pod-design__design-set-image-title horizontal-align-content-center">

                                                                    {printAreaRoles.find((roleItem) => roleItem.role === role.role).name}
                                                                     
                                                                    <div className="horizontal-align-content-center" style={{ gap: "0.35rem" }}>
 
                                                                        {currentProductRole?.edited === "Y" &&

                                                                             <InfoButton
                                                                                buttonIcon="🔒"
                                                                                shortLabel="Locked"
                                                                                description="This product has edited print area placement data."
                                                                            />

                                                                        }
                                                                      
                                                                    </div>
                                                                    
                                                                    <ToggleSwitch
                                                                        size="xs"
                                                                        name={String(configuredProductKey)}
                                                                        value={currentProductRole ? S2B(currentProductRole.active) : true}
                                                                        onChange={(event) => toggleDesignProductRoleActiveSate(event.target.value, role, configuredProduct)}
                                                                    />

                                                                </div>

                                                                :

                                                                <div className="pod-design__design-set-image-title horizontal-align-content-center">
                                                                </div>

                                                            }

                                                            <div></div>

                                                        </div>

                                                    </div>

                                                );

                                            })

                                            :

                                            getPodProductOrderedRoles(canonicalProduct).map((role) => {

                                                // Safely iterate surfaces
                                                const match = surfaces.some((surface) =>
                                                    (surface?.print_areas || []).find((printArea) =>
                                                        printArea?.role === role.role
                                                    )
                                                );

                                                // If there's no matching print area role for this product then return early
                                                if (!match) { return null; }

                                                // We want to show the role if it matches the basicRoleKey or if the role is selected in selectedRoles
                                                const roleIsSelected = selectedRoles.find((r) => r.role === role.role).selected;

                                                // Find the configured role whose settings define the available product views
                                                const currentProductRole = configuredProduct?.roles?.find((roleItem) => roleItem?.role === role?.role) || null;

                                                // Get the first live view allowed by this configured design product role
                                                const firstConfiguredView = getFirstConfiguredProductPreviewView(
                                                    canonicalProduct,
                                                    currentProductRole,
                                                    role?.role
                                                );

                                                // Do not substitute an unrelated canonical or custom-product view
                                                if (!firstConfiguredView) { return null; }

                                                if (roleIsSelected) {

                                                    return (

                                                        <div key={role.role} className="masonry_product">

                                                            <PodProductPreview
                                                                thedesign={{ role: role.role }}
                                                                product={canonicalProduct.custom_product}
                                                                currentProductRoleData={configuredProduct?.roles?.find((r) => r?.role === role.role) || null}
                                                                mockup_height={180}
                                                                options={{
                                                                    display_mode: "product",
                                                                    surface_code: firstConfiguredView.surface_code || "default",
                                                                    print_area_code: firstConfiguredView.print_area_code || "default",
                                                                    view_style: firstConfiguredView.style || "default",
                                                                    view_name: firstConfiguredView.name || "",
                                                                }}
                                                            />

                                                        </div>

                                                    );

                                                }

                                                return null;

                                            })

                                        }

                                    </div>

                                    {/* Summarise print quality problems across the parent and its children */}
                                    {productPrintQualityStatus &&

                                        <div
                                            className="pod-design__product-quality-warning"
                                            onClick={(event) => event.stopPropagation()}
                                        >

                                            <InfoButton
                                                buttonIcon={
                                                    <WarningIcon
                                                        className={`printQualityWarningIcon printQualityWarningIcon--${productPrintQualityStatus}`}
                                                    />
                                                }
                                                shortLabel="Print quality warning"
                                                description={productPrintQualityMessage}
                                            />

                                        </div>

                                    }

                                        {loadingPodProductDetails && canonicalProduct.sku === selectedProductSku && String(selectedConfiguredProductId || "") === String(configuredProductKey || "") &&

                                        <div className="pod-design__product-loading-container">

                                            <div style={{textAlign: "center"}}>
                                                <LoadingSpinner />
                                                <br/>
                                                Loading...
                                            </div>

                                        </div>

                                    }

                                    <div
                                        style={{
                                            width: "95%"
                                        }}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                       
                                        {!isStrichTemplate &&

                                            <CreateButton
                                                    tooltip="Delete Product"
                                                    isDelete={true}
                                                    size="xs"
                                                    icon="/images/icon_bin.png"
                                                    iconTitle="Delete Product"
                                                    onClick={(event) => {

                                                        // Stop the click bubbling up
                                                        event.stopPropagation();
                                                        event.preventDefault();
                                                        event.currentTarget.blur();

                                                        // Delete the configured product
                                                        handleDeleteConfiguredProduct({
                                                            configuredProduct: configuredProduct,
                                                            configuredProductIndex: configuredProductIndex,
                                                            configuredProductKey: configuredProductKey,
                                                            canonicalProduct: canonicalProduct,
                                                        });

                                                    }}
                                            />

                                        }

                                    </div>

                                </div>

                            );

                        })}

                    </div>

                </>

            }



            {/* Add product picker popup */}
            {addProductPickerIsOpen &&

                <div className="popup-overlay">
                    
                    <div className="popup-container popup-container--large" >

                        {/* This is the modal header */}
                        <div className="popup__header">

                            {/* This is the title area */}
                            <div className="popup__headerLeft">

                                {/* This is the parent title */}
                                <h2 className="popup__title">

                                    Add Products

                                </h2>

                                {/* This is the parent subtitle */}
                                <p className="popup__subtitle">

                                    Choose products to add to this {designOrTemplate}.

                                </p>

                            </div>

                            {/* This is the header actions */}
                            <div className="popup__headerRight">

                                {/* This is the close button */}
                                <button
                                    type="button"
                                    className="popup__close"
                                    onClick={() => closeAddProductPicker()}
                                    title="Close"
                                    disabled={addProductPickerPendingCount > 0 || addProductPickerIsClosing}
                                >
                                    🞬
                                </button>

                            </div>

                        </div>


                        <div className="popup-content popup-content__body">

                            <div className="addProductPickerLayout">

                                <div className="addProductPickerFilters">

                                    <div className="addProductPickerFilterHeader">

                                        <h4 className="addProductPickerFilterHeading">Filters</h4>

                                        <CreateButton
                                            title="Clear"
                                            invert={true}
                                            type="button"
                                            size="xs"
                                            onClick={clearAddProductFilters}
                                        />

                                    </div>

                                    <div className="addProductPickerFilterSections">

                                        <div>

                                            <h5 className="addProductPickerSectionTitle">Product Options</h5>

                                            <label className="addProductPickerLabel">
                                                Design Roles
                                            </label>

                                            <div className="addProductPickerAttributeValues">

                                                {designSetRoleOptions.map((roleOption) => (

                                                    <label key={roleOption.role} className="addProductPickerAttributeOption">

                                                        <input
                                                            type="checkbox"
                                                            checked={(addProductFilters.designRoles || []).includes(roleOption.role)}
                                                            onChange={() => {

                                                                // Toggle selected design role filter
                                                                setAddProductFilters((prevFilters) => ({
                                                                    ...prevFilters,
                                                                    designRoles: toggleArrayValue(prevFilters.designRoles || [], roleOption.role),
                                                                }));

                                                            }}
                                                        />

                                                        <span>
                                                            {roleOption.name}
                                                            {!roleOption.hasImageData ? " - missing image" : ""}
                                                        </span>

                                                    </label>

                                                ))}

                                            </div>

                                            <label className="addProductPickerLabel">
                                                Product Availability
                                            </label>

                                            <select
                                                className="addProductPickerSelect"
                                                value={addProductFilters.roleVisibility}
                                                onChange={(event) => setAddProductFilters((prevFilters) => ({ ...prevFilters, roleVisibility: event.target.value }))}
                                            >
                                                <option value="available">Can be added</option>
                                                <option value="all">Show unavailable too</option>
                                            </select>


                                            <label className="addProductPickerLabel">
                                                Search
                                            </label>

                                            <input
                                                type="text"
                                                className="addProductPickerInput"
                                                value={addProductFilters.search}
                                                onChange={(event) => setAddProductFilters((prevFilters) => ({ ...prevFilters, search: event.target.value }))}
                                                placeholder="Title, SKU or product code"
                                            />

                                            <label className="addProductPickerLabel">
                                                Price
                                            </label>

                                            <div className="addProductPickerPriceRange">

                                                <DualRangeSlider
                                                    min={Number(addProductPriceBounds.min)}
                                                    max={Number(addProductPriceBounds.max)}
                                                    step={0.01}
                                                    minValue={selectedMinPrice}
                                                    maxValue={selectedMaxPrice}
                                                    onChange={({ minValue, maxValue }) => {

                                                        // Store both selected price values
                                                        setAddProductFilters((prevFilters) => ({

                                                            // Preserve the existing filters
                                                            ...prevFilters,

                                                            // Store the selected minimum price
                                                            minPrice: String(minValue),

                                                            // Store the selected maximum price
                                                            maxPrice: String(maxValue),

                                                        }));

                                                    }}
                                                    label="Price range"
                                                    formatValue={(value) => `£${Number(value).toFixed(2)}`}
                                                />

                                            </div>

                                            <label className="addProductPickerLabel">
                                                Print Areas
                                            </label>

                                            <select
                                                className="addProductPickerSelect"
                                                value={addProductFilters.printAreas}
                                                onChange={(event) => setAddProductFilters((prevFilters) => ({ ...prevFilters, printAreas: event.target.value }))}
                                            >
                                                <option value="">Any</option>
                                                <option value="1">1 print area</option>
                                                <option value="2">2 print areas</option>
                                                <option value="3+">3+ print areas</option>
                                            </select>

                                            <label className="addProductPickerLabel">
                                                Colourways
                                            </label>

                                            <select
                                                className="addProductPickerSelect"
                                                value={addProductFilters.colourwayMode}
                                                onChange={(event) => setAddProductFilters((prevFilters) => ({ ...prevFilters, colourwayMode: event.target.value }))}
                                            >
                                                <option value="">Any</option>
                                                <option value="has">Has colourways</option>
                                                <option value="none">No colourways</option>
                                            </select>

                                            {addProductFilterOptions.colourways.length > 0 &&

                                                <div className="addProductPickerSwatches">

                                                    {addProductFilterOptions.colourways.map((colourway) => {

                                                        // Check whether this colourway is selected
                                                        const isSelected = (addProductFilters.colourways || []).includes(colourway.key);

                                                        // Return the colourway picker button
                                                        return (

                                                            <button
                                                                key={colourway.key}
                                                                type="button"
                                                                title={colourway.name}
                                                                className={`addProductPickerSwatch ${isSelected ? "addProductPickerSwatch--selected" : ""} ${colourway.hex_colour?.toLowerCase() === "#ffffff" ? "addProductPickerSwatch--white" : ""}`}
                                                                style={{ "--swatch-colour": colourway.hex_colour || "#fff" }}
                                                                onClick={() => toggleAddProductColourwayFilter(colourway.key)}
                                                            />

                                                        );

                                                    })}

                                                </div>

                                            }

                                        </div>

                                        {Object.keys(addProductFilterOptions.attributes).length > 0 &&

                                            <div>

                                                <h5 className="addProductPickerSectionTitle">Attributes</h5>

                                                {Object.entries(addProductFilterOptions.attributes).map(([attributeName, attributeValues]) => (

                                                    <div key={attributeName} className="addProductPickerAttributeGroup">

                                                        <div className="addProductPickerAttributeName">
                                                            {attributeName}
                                                        </div>

                                                        <div className="addProductPickerAttributeValues">

                                                            {attributeValues.map((attributeValue) => (

                                                                <label key={attributeValue} className="addProductPickerAttributeOption">

                                                                    <input
                                                                        type="checkbox"
                                                                        checked={(addProductFilters.attributes?.[attributeName] || []).includes(attributeValue)}
                                                                        onChange={() => toggleAddProductAttributeFilter(attributeName, attributeValue)}
                                                                    />

                                                                    <span>{attributeValue}</span>

                                                                </label>

                                                            ))}

                                                        </div>

                                                    </div>

                                                ))}

                                            </div>

                                        }

                                    </div>

                                    <p className="addProductPickerResultCount">
                                        Showing {filteredAddProductPickerProducts.length} of {sortedCanonicalProducts.length} products
                                    </p>

                                </div>

                                <div className="addProductPickerProducts">

                                    <div className="strich_masonry addProductPickerProductGrid">

                                        {filteredAddProductPickerProducts.map((canonicalProduct) => {




                                            // Get supported roles from parent and children
                                            const supportedRoleKeys = getCanonicalProductFamilySupportedRoleKeys(canonicalProduct);

                                            // Get active design role filters
                                            const activeDesignRoleFilters = Array.isArray(addProductFilters.designRoles) && addProductFilters.designRoles.length > 0
                                                ? addProductFilters.designRoles
                                                : designSetRoleOptions.map((roleOption) => roleOption.role);

                                            // Get matching filtered role keys whether or not they have image data
                                            const matchingFilteredRoleKeys = activeDesignRoleFilters.filter((roleKey) => supportedRoleKeys.has(String(roleKey)));

                                            // Count display roles from selected modal filters
                                            const displayRoleCount = matchingFilteredRoleKeys.length;

                                            // If this product family does not match the selected role filters, return early
                                            if (displayRoleCount < 1) { return null; }



                                           

                                            // Get matching usable selected roles
                                            const matchingUsableRoleKeys = getCanonicalProductFamilyMatchingUsableRoleKeys(canonicalProduct);

                                            // Check if product can be added
                                            const productCanBeAdded = matchingUsableRoleKeys.length > 0;

                                            // Check if it only matches selected roles that are missing image data
                                            const matchesMissingImageRole = designSetRoleOptions.some((roleOption) => {
                                                return !roleOption.hasImageData && supportedRoleKeys.has(roleOption.role);
                                            });

                                            // Work out unavailable reason
                                            const unavailableReason = !productCanBeAdded
                                                ? matchesMissingImageRole
                                                    ? "A matching printable role is selected, but it has no design image yet"
                                                    : "Does not include a printable role used by this design"
                                                : "";


                                            // Build the live design preview for this product card
                                            const previewData = getAddProductPickerPreviewData(
                                                canonicalProduct,
                                                matchingUsableRoleKeys
                                            );

                                            // Read the current card state
                                            const productCardState = String(
                                                addProductPickerProductStates[canonicalProduct.sku] || ""
                                            );

                                            // Lock only this card while it is adding or displaying its success state
                                            const productCardIsBusy = Boolean(productCardState);
                                            const productCardIsLocked = productCardIsBusy || addProductPickerIsClosing;

                                            // The card can be clicked when it is usable and not currently locked
                                            const productCardCanBeClicked = productCanBeAdded && !productCardIsLocked;

                                            // Build the card classes without changing the grid layout
                                            let productCardClassName = "strich_masonry_designset pod-design__product addProductPickerProductCard";

                                            if (productCanBeAdded) {
                                                productCardClassName += " addProductPickerProductCard--clickable";
                                            }

                                            if (!productCanBeAdded) {
                                                productCardClassName += " addProductPickerProductCard--unavailable";
                                            }

                                            if (productCardState === "adding") {
                                                productCardClassName += " addProductPickerProductCard--adding";
                                            }

                                            if (productCardState === "added") {
                                                productCardClassName += " addProductPickerProductCard--added";
                                            }

                                            if (addProductPickerIsClosing) {
                                                productCardClassName += " addProductPickerProductCard--closing";
                                            }

                                            // Build keyboard and tooltip values for the clickable card
                                            let productCardTabIndex = -1;
                                            let productCardTitle = unavailableReason;

                                            if (productCanBeAdded) {
                                                productCardTabIndex = 0;
                                                productCardTitle = "Add Product";
                                            }

                                            // Build the status wording and class
                                            let productStatusText = "";
                                            let productStatusClassName = "addProductPickerProductStatus";

                                            if (productCardState === "adding") {
                                                productStatusText = "Adding...";
                                                productStatusClassName += " addProductPickerProductStatus--adding";
                                            }

                                            if (productCardState === "added") {
                                                productStatusText = "Added";
                                                productStatusClassName += " addProductPickerProductStatus--added";
                                            }

                                            // Return the product card
                                            return (

                                                <div
                                                    key={canonicalProduct.sku}
                                                    className={productCardClassName}
                                                    role="button"
                                                    tabIndex={productCardTabIndex}
                                                    title={productCardTitle}
                                                    aria-disabled={!productCardCanBeClicked}
                                                    onClick={() => {

                                                        // Stop unavailable or busy cards being added
                                                        if (!productCardCanBeClicked) { return; }

                                                        // Add this product independently
                                                        handleAddProductFromPicker(canonicalProduct);

                                                    }}
                                                    onKeyDown={(event) => {

                                                        // Only respond to the normal keyboard activation keys
                                                        if (
                                                            event.key !== "Enter" &&
                                                            event.key !== " "
                                                        ) {
                                                            return;
                                                        }

                                                        // Stop the page moving when space activates the card
                                                        event.preventDefault();

                                                        // Stop unavailable or busy cards being added
                                                        if (!productCardCanBeClicked) { return; }

                                                        // Add this product independently
                                                        handleAddProductFromPicker(canonicalProduct);

                                                    }}
                                                >

                                                    <div className="addProductPickerProductTitle">

                                                        <p>
                                                            <b>{canonicalProduct.Title}</b>
                                                        </p>

                                                    </div>

                                                    <div className="addProductPickerProductImages">

                                                        <div className="masonry_product">

                                                            {previewData &&

                                                                <PodProductPreview
                                                                    thedesign={previewData.designRole}
                                                                    product={previewData.canonicalProduct.custom_product}
                                                                    currentProductRoleData={previewData.configuredRole}
                                                                    mockup_height={140}
                                                                    options={{
                                                                        display_mode: "product",
                                                                        surface_code: previewData.view.surface_code || "default",
                                                                        print_area_code: previewData.view.print_area_code || "default",
                                                                        view_style: previewData.view.style || "default",
                                                                        view_name: previewData.view.name || "",
                                                                        selected_colourway_code: previewData.selectedColourwayCode,
                                                                    }}
                                                                />

                                                            }

                                                            {!previewData &&

                                                                <PodProductPreview
                                                                    thedesign={{ role: basicDesignRoleKey }}
                                                                    product={canonicalProduct.custom_product}
                                                                    currentProductRoleData={null}
                                                                    mockup_height={140}
                                                                    options={{
                                                                        display_mode: "product",
                                                                        surface_code: "default",
                                                                        view_style: "default",
                                                                    }}
                                                                />

                                                            }

                                                        </div>

                                                    </div>

                                                    {!productCanBeAdded &&

                                                        <p className="addProductPickerProductUnavailable">
                                                            {unavailableReason}
                                                        </p>

                                                    }

                                                    {productCardState &&

                                                        <div className={productStatusClassName}>
                                                            <span>{productStatusText}</span>
                                                        </div>

                                                    }

                                                </div>

                                            );

                                        })}

                                    </div>

                                </div>

                            </div>

                        </div>

                        {/* Keep the close action in a consistent bottom-right position */}
                        <div className="addProductPickerFooter">

                            <div className="addProductPickerFooterStatus">

                                {addProductPickerPendingCount > 0 &&
                                    <span>Adding products...</span>
                                }

                                {addProductPickerIsClosing &&
                                    <span>Refreshing products...</span>
                                }

                            </div>

                            <CreateButton
                                title="Done"
                                type="button"
                                size="s"
                                onClick={() => closeAddProductPicker()}
                                disabled={addProductPickerPendingCount > 0 || addProductPickerIsClosing}
                            />

                        </div>

                    </div>

                </div>

            }









            {/* Open pop up with large image carousel (zoom effect) */}
            {imageCarouselPopupOpen &&

                <ImageCarouselPopup
                    images={allMockupImages}
                    initialImage={selectedMockupImage}
                    onClose={() => setImageCarouselPopupOpen(false)}
                />

            }

            {/* Render the product details popup when the user clicks a configured product */}
            {productDetailsPopupIsOpen &&
                podProductTranslations &&
                selectedProductSku &&
                podProductDetails?.sku &&
                String(podProductDetails.sku) === String(selectedProductSku) &&

                <ProductDetailsPopUp
                    isOpen={productDetailsPopupIsOpen}
                    onClose={() => {

                        // Clear the selected sku
                        setSelectedProductSku(null);

                        // Clear the selected configured product id
                        setSelectedConfiguredProductId(null);

                        // Close the pop up
                        setProductDetailsPopupIsOpen(false);

                    }}
                    product={podProductDetails}
                    translations={podProductTranslations}
                    podDesignDetails={podDesignDetails}
                    setPodProductDetails={setPodProductDetails}
                    handleCarouselLargeImages={handleCarouselLargeImages}
                    setCurrentProducts={setCurrentProducts}
                    printAreaRoles={printAreaRoles}
                    selectedRoles={selectedRoles}
                    latestTransform={latestTransform}
                    setLatestTransform={setLatestTransform}
                    currentProduct={currentProducts.find((currentProd) => {

                        // Match by configured product id when available
                        if (selectedConfiguredProductId) {
                            return String(currentProd?.id || "") === String(selectedConfiguredProductId || "") || String(currentProd?.client_configured_product_id || "") === String(selectedConfiguredProductId || "");
                        }

                        // No configured product id means no safe current-product match
                        return false;

                    })}
                    saveTransformDataToProductRole={saveTransformDataToProductRole}
                    // Pass form values so we can access the latest design title
                    formValues={formValues}
                    productTitleWithoutPlaceholders={canonicalProducts?.find((prod) => prod.sku === selectedProductSku)?.Title}
                    storeHomeCountry={storeHomeCountry}
                    designOrTemplate={designOrTemplate}
                    onProductFamilyCommitted={onProductFamilyCommitted}

                />

            }

        </div>

    )

}; export default PodProductSelector;
